/**
 * M4 strategic core contracts (spec §3-§14; plan E0). Every record, SoA store, const enum and protocol type
 * of the core lane lives here, frozen at the fork commit so waves 1-5 build in parallel.
 *
 * Conventions:
 * - Const objects, never TS enums (`erasableSyntaxOnly`). Numeric codes are append-only (M5 appends).
 * - Inside the core everything is a numeric index into a store; string ids only cross the boundary
 *   (views, commands, saves, the manifest). `ids.ts` formats and parses them.
 * - Stores are structure-of-arrays allocated once at capacity by `state.ts` (`allocateState`). Per-tick
 *   code mutates them in place and allocates nothing (D-014, spec §3.7).
 * - Per-ware arrays are laid out `index × WARE_COUNT + ware` (`economy/stock.ts` `sw`); per-faction arrays
 *   `index × factionCapacity + faction`.
 * - Times are strategic seconds (float64). Positions are float64 metres in M3's system frame (star at the
 *   origin, y up; `galaxyToWorld([gx, gy, gz]) = [gx, gz, −gy]`).
 */
import type { Rng } from '../core/rng'
import type { Galaxy } from '../galaxy/types'
import type { CatchUpPlayer } from './catchup'
import type { StrategicData } from './data'
import type { PlayerDrawCommand } from './economy/market'
// M10 P5a11a: type-only (the cradle pair's commands live with their Zod schema, P5a10w D-1073).
import type { CarrierCommand } from './schemas'
import { WARE_COUNT, type ComponentType, type WareId } from './wares'

// ---------------------------------------------------------------------------------------------
// Factions, roles and codes
// ---------------------------------------------------------------------------------------------

/** Strategic faction ids (factions.md data ids, spec §4.7). The index is the position here and in factions.json. */
export const FACTION_IDS = ['raider', 'authority', 'tethys', 'meridian', 'varro_security', 'mutual', 'holdfast', 'guardian'] as const
export type FactionId = (typeof FACTION_IDS)[number]
export const FACTION = {
  raider: 0,
  authority: 1,
  tethys: 2,
  meridian: 3,
  varro_security: 4,
  mutual: 5,
  holdfast: 6,
  guardian: 7,
} as const satisfies Record<FactionId, number>
/** Faction field value meaning "no faction" (events, killers). */
export const FACTION_NONE = 255
/** Faction field value meaning "the player" (the player is not a strategic faction in M4; M6 adds one). */
export const FACTION_PLAYER = 254

/**
 * M8 (MC-f5c, D-TBD T45): the factions whose ships leave parts with a maker: every strategic faction but the raiders
 * and the guardian cell (D-430: guardian wrecks carry no maker). A subset of the career `MakerId`.
 */
export type WreckMakerId = Exclude<FactionId, 'raider' | 'guardian'>
export const WRECK_MAKER_IDS = ['authority', 'tethys', 'meridian', 'varro_security', 'mutual', 'holdfast'] as const satisfies readonly WreckMakerId[]
/** Wreck owner value meaning "no maker" (raider, guardian, player and corporation ships; every v4 wreck). */
export const WRECK_OWNER_NONE = -1

/**
 * M9 (CH-1a, D-TBD M9-1): an off-screen wreck's claim state. `none` (0) is an ordinary wreck; `disabled` (1) is a
 * claimable hull left by a disabled loss (CH-1b rolls it); `open` (2) is a hulk the player has an open claim on
 * (exempt from the cap and the expiry until the kit completes or the claim is abandoned, MS-1). The saved record
 * writes `claim` only when it is not 0.
 */
export const WRECK_CLAIM = { none: 0, disabled: 1, open: 2 } as const
/** A saved wreck record's `claim` (absent = `WRECK_CLAIM.none`). */
export type WreckClaim = typeof WRECK_CLAIM.disabled | typeof WRECK_CLAIM.open

/** Agent roles (spec §7.5). */
export const ROLE = { trader: 0, miner: 1, supplier: 2, patrol: 3, convoy: 4, raider: 5, constructor: 6 } as const
export type RoleId = keyof typeof ROLE
export const ROLE_IDS = ['trader', 'miner', 'supplier', 'patrol', 'convoy', 'raider', 'constructor'] as const satisfies readonly RoleId[]

/** Agent states (spec §7.1). */
export const AGENT_STATE = {
  /** Needs a decision now. */
  idle: 0,
  /** Docked at `nodeTo`'s station until `t1`. */
  docked: 1,
  /** In-system leg from `nodeFrom` to `nodeTo`, `t0` → `t1`. */
  leg: 2,
  /** Relay transit from `nodeFrom` (exit relay) to `nodeTo` (entry relay in the next system). */
  jump: 3,
  /** Mining at the field node `nodeTo` until `t1`. */
  mining: 4,
  /** Patrol hold or raider lurk at `nodeTo` until `t1`. */
  holding: 5,
  /** Follows its group leader (no heap entry of its own). */
  member: 6,
  dead: 7,
} as const
export type AgentStateCode = (typeof AGENT_STATE)[keyof typeof AGENT_STATE]
export type AgentStateName = keyof typeof AGENT_STATE
export const AGENT_STATE_NAMES = ['idle', 'docked', 'leg', 'jump', 'mining', 'holding', 'member', 'dead'] as const satisfies readonly AgentStateName[]

/** `AgentPool.flags` bits (spec §7.1). */
export const AGENT_FLAG = {
  /** Can mine tetherstone (fracture rig). */
  fractureRig: 1,
  /** Frozen: in the player's system; the local layer executes its steps (no heap events, no abstract combat). */
  local: 2,
  /** Member or leader of a group. */
  inGroup: 4,
  /** Kept docked and making no decisions (repairing, waiting for its group). */
  grounded: 8,
} as const

/** What a leg is for (the `LocalStep` purposes, spec §10.1). Stored in `AgentPool.purpose`. */
export const LEG_PURPOSE = { trade: 0, deliver: 1, mine: 2, patrol: 3, exit: 4, lurk: 5 } as const
export type LegPurposeName = keyof typeof LEG_PURPOSE
export const LEG_PURPOSE_NAMES = ['trade', 'deliver', 'mine', 'patrol', 'exit', 'lurk'] as const satisfies readonly LegPurposeName[]

/** Node kinds of the in-system graph (spec §7.3). */
export const NODE_KIND = { station: 0, field: 1, relay: 2, gate: 3, base: 4 } as const
export type NodeKindName = keyof typeof NODE_KIND
export const NODE_KIND_NAMES = ['station', 'field', 'relay', 'gate', 'base'] as const satisfies readonly NodeKindName[]

/** Strategic field kinds (the M3 `Field.kind`). */
export const FIELD_KIND = { asteroid: 0, ice: 1, gas: 2 } as const
export const FIELD_KIND_NAMES = ['asteroid', 'ice', 'gas'] as const satisfies readonly (keyof typeof FIELD_KIND)[]

/** Production line status (spec §6.1, §11.2). */
export const LINE_STATUS = { idle: 0, running: 1, starved: 2, blocked: 3 } as const
export type LineStatusName = keyof typeof LINE_STATUS
export const LINE_STATUS_NAMES = ['idle', 'running', 'starved', 'blocked'] as const satisfies readonly LineStatusName[]

/** Group kinds (spec §7.4). Append-only (M5 adds war bands). */
export const GROUP_KIND = { convoyImport: 0, convoyExport: 1, raid: 2, patrolResponse: 3 } as const
export type GroupKindName = keyof typeof GROUP_KIND
export const GROUP_KIND_NAMES = ['convoyImport', 'convoyExport', 'raid', 'patrolResponse'] as const satisfies readonly GroupKindName[]

/** Group states: the role behaviours (E9) own the transitions. */
export const GROUP_STATE = { forming: 0, outbound: 1, holding: 2, returning: 3, done: 4 } as const

/** Trade side from the trader's (agent's or player's) point of view: `sell` = to the station, `buy` = from it. */
export const SIDE = { sell: 0, buy: 1 } as const
export type SideName = keyof typeof SIDE

/** `StationStore.trade` bits per station-ware, from the station's point of view (spec §4.2: bit 0 buys, bit 1 sells). */
export const TRADE_BIT = {
  buys: 1,
  sells: 2,
  /** Resold only to agents of the station owner (Tethys collecting tetherstone, spec §4.2). */
  ownerOnly: 4,
  /**
   * 5(e) (MC-e7b4, D-TBD): never sold to the player side (the pilot, the player corporation's ships, suppliers and works,
   * and a no-faction quote, which is the price the pilot sees) unless it owns the station; every AI faction's agents buy
   * it. The refractory alloy at a refinery (RD6: the pilot's alloy comes from the gate import terminal; the AI economy
   * moves it between factions).
   */
  noPilot: 8,
} as const

/** `StationStore.flags` bits, derived from the template's modules. */
export const STATION_FLAG = {
  dockable: 1,
  hidden: 2,
  habitat: 4,
  shipyard: 8,
  fence: 16,
  exportTerminal: 32,
  tradingPost: 64,
  /** Buys M2 salvage components (a template `componentMult`, spec §5.7). */
  buysComponents: 128,
} as const

/** Faction ledger categories (spec §7.7). Append-only (M5 adds tariffs, tolls, subsidy, fees). */
export const LEDGER = { sales: 0, purchases: 1, shipsBought: 2, shipsSold: 3, construction: 4, exports: 5, imports: 6, loot: 7 } as const
export type LedgerCategory = keyof typeof LEDGER
export const LEDGER_NAMES = [
  'sales',
  'purchases',
  'shipsBought',
  'shipsSold',
  'construction',
  'exports',
  'imports',
  'loot',
] as const satisfies readonly LedgerCategory[]
/**
 * The day-ledger width: M4 8, M5 15 (save v2), M6 21 = `LEDGER_COUNT_M6` (save v3, plan P16), M10 22 =
 * `LEDGER_COUNT_M10` (M10 P3a1, save v8: the `royalty` transfer column, booked from P4a1c; maker ← non-maker yard owner).
 */
export const LEDGER_COUNT = 22

/** Construction site stages (spec §6.5). `planned` holds a seeded build plan until its start day. */
export const SITE_STAGE = { planned: 0, supplying: 1, building: 2, done: 3 } as const
export type SiteStageName = keyof typeof SITE_STAGE
export const SITE_STAGE_NAMES = ['planned', 'supplying', 'building', 'done'] as const satisfies readonly SiteStageName[]

/**
 * `SiteStore.works` values (M8 SNK-6a, SNK-8): not a works site, a surplus works site, a refit works site; LV WK-1b
 * (D5, review M1) appends a megaproject stage site (`works.megaproject`). Append-only (the saved `sites.works` column).
 */
export const SITE_WORKS = { none: 0, works: 1, refit: 2, megaproject: 3 } as const

/** Shipyard order states (spec §6.4). */
export const ORDER_STATE = { queued: 0, building: 1 } as const

/** Out-of-system encounter kinds (spec §8.1). Append-only (M5 adds war bands and zones). */
export const ENCOUNTER_KIND = { interception: 0, patrolMeet: 1 } as const

/** Wire cause kinds (spec §12.2), in scoring order. */
export const CAUSE_KIND = {
  lostCarrier: 0,
  upstreamStarved: 1,
  raidNearby: 2,
  demandJump: 3,
  playerDump: 4,
  strongDemand: 5,
  oversupply: 6,
} as const
export type CauseKindName = keyof typeof CAUSE_KIND
export const CAUSE_KIND_NAMES = [
  'lostCarrier',
  'upstreamStarved',
  'raidNearby',
  'demandJump',
  'playerDump',
  'strongDemand',
  'oversupply',
] as const satisfies readonly CauseKindName[]

/** Routing: hop count meaning "unreachable over online relays". */
export const UNREACHABLE = 255

// ---------------------------------------------------------------------------------------------
// Strategic events (spec §11.4)
// ---------------------------------------------------------------------------------------------

/**
 * Strategic event types, numbered and append-only (M5 appends; readers skip types they don't know).
 * Field conventions (unused fields: station/agent/aux −1, ware −1, faction/other FACTION_NONE, numbers 0):
 * - `tradeDone`: sys, station, agent (−1 = the player, or no ship when faction is an AI faction: a refit works
 *   site's market draw, SNK-8c, never mirrored to the notable ring: SNK-8e), faction = the trader's (FACTION_PLAYER for the player), other = the station owner, ware, qty t, value = total credits, cause = SIDE (sell = to the
 *   station), aux = the agent's seq.
 * - `stationStarved` / `stationRecovered`: sys, station, faction = owner, ware = first missing input
 *   (starved) or −1, cause = LINE_STATUS (starved or blocked), aux = line index.
 * - `shortage` / `surplus`: sys, station, faction = owner, ware, qty = stock, value = the day's fill (max for
 *   a shortage, min for a surplus).
 * - `orderPlaced`: sys, station = yard, faction = the ordering faction, other = the yard owner, value =
 *   price, cause = hull index, aux = order seq.
 * - `shipBuilt`: sys, station = yard, agent = the new agent, faction, other = ROLE, cause = hull index,
 *   aux = the agent's seq.
 * - `agentDestroyed`: sys, station = the station it was carrying to (−1), agent, faction = victim, other =
 *   killer faction (FACTION_PLAYER, FACTION_NONE), ware = cargo ware, qty = cargo lost t, value = loss
 *   value (hull + cargo at base prices), cause = ROLE, aux = the agent's seq.
 * - `raid`: sys, agent = group leader, faction = raider faction, other = the target's faction, ware /
 *   qty = the target's cargo, aux = group seq.
 * - `convoyDeparted` / `convoyArrived` / `convoyLost`: sys, station = destination, agent = leader, faction,
 *   ware, qty = cargo t, cause = GROUP_KIND, aux = group seq.
 * - `exportShipped`: sys, station = the terminal, faction = paid faction, ware, qty, value = credits in.
 * - `importArrived`: sys, station = destination, faction = paying faction, ware, qty, value = credits out.
 * - `constructionStarted` / `stationBuilt`: sys, station (the new station, stationBuilt), faction = owner,
 *   cause = template index, aux = site seq.
 * - `fieldDepleted` / `fieldRecovered`: sys, ware = ore, qty = reserve t, aux = field index.
 * - `priceShock`: sys, station, faction = owner, ware, qty = unit price, value = signed delta fraction.
 * - `wreckCreated`: sys, agent = the lost agent, ware / qty = cargo in the wreck, cause = hull index,
 *   aux = wreck seq.
 * - `gateStability` (hidden): value = stability, qty = tetherstone extracted that day.
 */
export const EV = {
  tradeDone: 1,
  stationStarved: 2,
  stationRecovered: 3,
  shortage: 4,
  surplus: 5,
  orderPlaced: 6,
  shipBuilt: 7,
  agentDestroyed: 8,
  raid: 9,
  convoyDeparted: 10,
  convoyArrived: 11,
  convoyLost: 12,
  exportShipped: 13,
  importArrived: 14,
  constructionStarted: 15,
  stationBuilt: 16,
  fieldDepleted: 17,
  fieldRecovered: 18,
  priceShock: 19,
  wreckCreated: 20,
  gateStability: 21,
  // M5 (M5 spec §16). Field conventions: see "M5 event fields" at the M5 block below. `varroStoke` and
  // `holdfastFundsGuardians` are hidden (`events.ts` `HIDDEN_EVENT`): never on the Wire, in views or the UI.
  claimChanged: 22,
  claimContested: 23,
  stateStaged: 24,
  relationChanged: 25,
  stationDisabled: 26,
  stationEnabled: 27,
  stationCaptured: 28,
  zoneOpened: 29,
  zoneClosed: 30,
  voteScheduled: 31,
  voteResolved: 32,
  gateStage: 33,
  gateClosure: 34,
  gateReopened: 35,
  crime: 36,
  bountyChanged: 37,
  factionAction: 38,
  sweep: 39,
  guardianRaid: 40,
  tetherstoneSeized: 41,
  varroStoke: 42,
  holdfastFundsGuardians: 43,
  // M6 (M6 spec §5-§8; plan P0). Field conventions: see "M6 event fields" at the M6 block below. None is hidden.
  shipBought: 44,
  orderSet: 45,
  captainHired: 46,
  captainFired: 47,
  captainQuit: 48,
  captainDeserted: 49,
  captainDied: 50,
  captainRecovered: 51,
  skillUp: 52,
  strike: 53,
  wagesLate: 54,
  corpFounded: 55,
  stationCommissioned: 56,
  commissionBuilt: 57,
  stationUpgraded: 58,
  milestone: 59,
  assetAttacked: 60,
  fleetEarning: 61,
  orderStopped: 62,
  charterBought: 63,
  shipTransferred: 64,
  insurancePaid: 65,
  // M7 (plan §2, S16). Fields: see "M7 event fields" at the M7 block. None is hidden; S17–S24 emit them.
  storyTierDrop: 66,
  grievanceSet: 67,
  floorExtended: 68,
  repealProposed: 69,
  stationRestored: 70,
  stationDark: 71,
  stationReverted: 72,
  registryChallenge: 73,
  registrationRefused: 74,
  // M8 (plan 2026-10-04 SNK-6a, D-TBD T30). Fields: see "M8 event fields" below the M7 ones. Not hidden.
  worksOpened: 75,
  // LV RD-B2 (D-828 U3). Fields: see the RD-B2 note below the M8 ones. Not hidden; not read back (save.ts READ_BACK).
  clanReformed: 76,
} as const
export type EventType = (typeof EV)[keyof typeof EV]

/** The SoA event ring (spec §11.4). Written by `events.ts` `emit`; read with cursors (`firstReadable`). */
export interface StrategicEventRing {
  /** A power of two. */
  capacity: number
  mask: number
  type: Uint8Array
  t: Float64Array
  sys: Uint16Array
  station: Int32Array
  agent: Int32Array
  faction: Uint8Array
  other: Uint8Array
  ware: Int8Array
  qty: Float32Array
  value: Float32Array
  cause: Uint8Array
  aux: Int32Array
  /** Total events ever emitted. Event `i` lives in slot `i & mask` while `i >= head - capacity`. */
  head: number
  /** A second ring that receives a copy of selected events (the notable ring), or null. */
  mirror: StrategicEventRing | null
  /** Per event type: 1 = copy to the mirror. Events with `agent < 0` (the player's trades) are always copied. */
  mirrorMask: Uint8Array
}

// ---------------------------------------------------------------------------------------------
// Stores (spec §7.1, §4; allocated by state.ts)
// ---------------------------------------------------------------------------------------------

/** Capacities of every store. Defaults come from strategic.json `caps`; systems and relays from the galaxy. */
export interface StateDims {
  agents: number
  maxHops: number
  stations: number
  linesPerStation: number
  nodes: number
  fields: number
  groups: number
  groupMembers: number
  orders: number
  wrecks: number
  sites: number
  encounters: number
  encounterSide: number
  factions: number
  heap: number
  events: number
  notable: number
  systems: number
  relays: number
}

/**
 * The agent pool (spec §7.1), SoA with a free-index stack. `allocAgent` pops the lowest free index first
 * (the stack is pre-filled in reverse), sets `alive`, a fresh `seq` and `seed`, and resets every column.
 * `freeAgent` marks it dead, bumps `gen` (so stale heap entries die) and pushes the index back.
 */
export interface AgentPool {
  capacity: number
  /** Live agents. */
  count: number
  /** One past the highest index ever allocated (saves trim columns to it). */
  high: number
  freeTop: number
  free: Int32Array
  /** Next agent serial: agent ids are `ag-<seq>` (saved as `counters.agentSeq`). */
  nextSeq: number
  /** The world seed agent seeds derive from: `seed[i] = deriveSeed(worldSeed, 'agent', seq)`. */
  worldSeed: number
  /** Systems per route (`route` holds `capacity × maxHops`). */
  maxHops: number
  alive: Uint8Array
  role: Uint8Array
  faction: Uint8Array
  hull: Uint8Array
  /** AGENT_STATE. */
  state: Uint8Array
  /** AGENT_FLAG bits. */
  flags: Uint8Array
  /** Group index, −1 = none. */
  group: Int32Array
  /** Home station index, −1 = none. */
  home: Int32Array
  /** Current system (the leg's system). */
  sys: Uint16Array
  nodeFrom: Int32Array
  nodeTo: Int32Array
  /** Current step start and end (eta), strategic seconds. */
  t0: Float64Array
  t1: Float64Array
  /** Scheduler generation (lazy deletion). */
  gen: Uint32Array
  /** 0..1. */
  hp: Float32Array
  /**
   * The abstract munitions magazine, 0..1 (1 = full; M8 SNK-4a, save v5). Every agent spawns and resets full; the
   * combat spend (SNK-4b1) and the dock refill (SNK-4b2) write it. Layout only until gate A (excluded from `aiHash`).
   */
  mag: Float32Array
  /** One cargo lot (a single ware; −1 = empty) and its tonnes. */
  cargoWare: Int8Array
  cargoQty: Float32Array
  /** Station indices of the current job: where it buys (or loads) and where it sells (or delivers); −1 = none. */
  src: Int32Array
  dst: Int32Array
  /** The unit price quoted (and reserved) at `src`. */
  quote: Float32Array
  /** Tonnes this agent has reserved at `src` (or of a field's reserve, for a miner). */
  reserved: Float32Array
  /** Tonnes this agent has added to `dst`'s incoming. */
  incoming: Float32Array
  /** The ware of the current job (−1 = none). The cargo ware once loaded. */
  ware: Int8Array
  /** Route systems: `route[i * maxHops + k]`. */
  route: Uint16Array
  routeLen: Uint8Array
  routePos: Uint8Array
  /** Role job: field index (miner), site index (constructor), assigned system (patrol); −1 = none. */
  job: Int32Array
  /** Role sub-state, owned by the role's behaviour (e.g. heading to the source vs the sink). */
  phase: Uint8Array
  /** LEG_PURPOSE of the current step. */
  purpose: Uint8Array
  /**
   * Role timer, strategic seconds: the expiry of a held reservation or incoming (traders, miners,
   * suppliers, constructors: eta + reservationGraceS), the next re-evaluation or response end (patrols),
   * the next sortie or the lurk end (raiders).
   */
  timer: Float64Array
  /** The last progress the local layer synced along the current step (0..1; spec §10.1). */
  progress: Float32Array
  /** Stable serial: agent id = `ag-<seq>`. */
  seq: Uint32Array
  seed: Uint32Array
}

/** Stations (spec §4.2-§4.4, §6). Stations are never freed in M4; `count` only grows. */
export interface StationStore {
  capacity: number
  count: number
  /** Boundary ids (`sys-07-st2`, adopted M3 ids, or M2's `hub`/`refinery`/`scrapyard` in the fallback). */
  id: string[]
  name: string[]
  assetId: string[]
  /** Template index (templates.json order). */
  template: Uint8Array
  level: Uint8Array
  /** Owner faction index. */
  owner: Uint8Array
  /**
   * The owner the station's layout was configured for (built or re-levelled): owner-only modules (the Tethys
   * tetherstone collect) follow it, and a capture changes `owner` only. Saved where it differs (D-TBD).
   */
  configOwner: Uint8Array
  /**
   * The faction whose lines a yard builds: its owner when built; a capture changes `owner` only; M10 spec §4.6. Set
   * once by `createStation`, never by `configureStation` or `relevelStation`; saved as `stations.cols.tooling` (M10 P3a2).
   */
  tooling: Uint8Array
  sys: Uint16Array
  node: Int32Array
  /** STATION_FLAG bits (16 wide: M5 adds state flags). */
  flags: Uint16Array
  /** Habitat module level (0 = none) and satisfaction 0..1 (spec §6.3). */
  habitatLevel: Uint8Array
  satisfaction: Float32Array
  /** Shipyard build slots (0 = not a yard). */
  yardSlots: Uint8Array
  /** Shipyard FIFO: first and last queued order index, −1 = empty (`OrderStore.next` links them). */
  orderHead: Int32Array
  orderTail: Int32Array
  /** Workforce multiplier 1..1.5 (spec §6.2), written by the habitat step. */
  workforceMult: Float32Array
  /** Production state multiplier: 1 in M4 (M5 states change it). */
  stateMult: Float32Array
  /** Starved or blocked line-seconds today (rollup input). */
  dayStarvedS: Float32Array
  // Per station × ware (stations × WARE_COUNT):
  /** TRADE_BIT bits. */
  trade: Uint8Array
  stock: Float32Array
  reserved: Float32Array
  incoming: Float32Array
  /** Capacity per ware, t. */
  cap: Float32Array
  /** 3-day EMA of fill, updated at the day rollup (spec §5.5). */
  baseline: Float32Array
  /** Tonnes the station bought (in) and sold (out) today. */
  dayIn: Float32Array
  dayOut: Float32Array
  /** The lowest and highest fill seen today (maintained by `stock.ts`; reset at the day rollup). */
  dayFillMin: Float32Array
  dayFillMax: Float32Array
  /**
   * Balance round 2 (G3a): cumulative tonnes production actually added (after the `partialOutput` clamp), per
   * station × ware. **Runtime-only, never saved** (zero after a load); the balance recorder takes deltas.
   */
  addedT: Float64Array
}

/** Production lines (spec §6.1): `perStation` slots per station; line `l` of station `s` is `s × perStation + l`. */
export interface LineStore {
  perStation: number
  /** Lines in use per station. */
  count: Uint8Array
  /** Recipe index (StrategicData.recipes), −1 = empty slot. */
  recipe: Int16Array
  /** Quantity multiplier (the module level). */
  level: Uint8Array
  /** Seconds into the current cycle. */
  progress: Float32Array
  /** 1 = a cycle is in progress (its inputs were consumed at the start). */
  running: Uint8Array
  /** LINE_STATUS. */
  status: Uint8Array
  /** First missing input while starved, −1 = none. */
  missing: Int8Array
  /** Continuous starved or blocked seconds. */
  starvedS: Float32Array
  /** 1 = `stationStarved` was raised and `stationRecovered` is pending. */
  flagged: Uint8Array
  /**
   * Balance round 2 (G3a): cumulative completed cycles (fine and bulk), and of those the cycles whose outputs did
   * not all fit (the `partialOutput` clamp). **Runtime-only, never saved** (zero after a load); the balance
   * recorder takes deltas.
   */
  cycles: Float64Array
  clampedCycles: Float64Array
}

/**
 * In-system nodes (spec §7.3). Each system owns a fixed block of `stride` node slots:
 * node `firstOfSystem[k] + j` for `j < countOfSystem[k]`. `state.ts` `addNode` appends to a system's block.
 */
export interface NodeTable {
  capacity: number
  /** Node slots per system (`floor(capacity / systems)`). */
  stride: number
  sys: Uint16Array
  /** NODE_KIND. */
  kind: Uint8Array
  /** Station index (station; base = the fence station once seeded, −1 before), field index (field), relay index (relay), −1 (gate). */
  ref: Int32Array
  /** Float64 system coordinates, m. */
  x: Float64Array
  y: Float64Array
  z: Float64Array
  /** Per system. */
  firstOfSystem: Int32Array
  countOfSystem: Uint16Array
}

/** Strategic fields and reserves (spec §4.5). Per-ore arrays are `field × RAW_WARE_COUNT + rawWare`. */
export interface FieldStore {
  capacity: number
  count: number
  /** The M3 field id. */
  id: string[]
  sys: Uint16Array
  node: Int32Array
  /** FIELD_KIND. */
  kind: Uint8Array
  radiusM: Float32Array
  /**
   * Ore weight from the generator (availability = clamp(weight / ref, min, 1)): `oreWeights`, or with
   * `world.mineralsVersion` 1 the M8 `mineralWeights ?? oreWeights` (MC-a4).
   */
  weight: Float32Array
  reserve: Float32Array
  initial: Float32Array
  reserved: Float32Array
  /** 1 = `fieldDepleted` raised, waiting for `fieldRecovered`. */
  depleted: Uint8Array
  /**
   * Per field: the deposit grade multiplier (galaxy.json `minerals.grades.mult`) on its reserves and its regrowth
   * share, tetherstone excepted (MC-a4). 1 for every field with `world.mineralsVersion` 0, and for fields without a
   * grade (ice, gas). Derived at world build, never saved.
   */
  gradeMult: Float32Array
}

/** Groups: convoys, raider groups, patrol responses (spec §7.4). Only the leader has heap entries. */
export interface GroupStore {
  capacity: number
  memberCapacity: number
  count: number
  high: number
  /** Free-index stack, pre-filled so the lowest index pops first. */
  free: Int32Array
  freeTop: number
  alive: Uint8Array
  /** GROUP_KIND. */
  kind: Uint8Array
  /** GROUP_STATE. */
  state: Uint8Array
  faction: Uint8Array
  leader: Int32Array
  /** `g × memberCapacity + k` agent indices (the leader included or not, as the behaviour decides). */
  members: Int32Array
  memberCount: Uint8Array
  /** Target system, node or station, by kind. */
  target: Int32Array
  /** Home station (raider base, patrol home), −1 = none. */
  home: Int32Array
  timer: Float64Array
  /** Group id = `grp-<seq>`. */
  seq: Uint32Array
}

/** Shipyard orders (spec §6.4), FIFO per yard through `next`. */
export interface OrderStore {
  capacity: number
  count: number
  high: number
  free: Int32Array
  freeTop: number
  alive: Uint8Array
  /** ORDER_STATE. */
  state: Uint8Array
  faction: Uint8Array
  hull: Uint8Array
  role: Uint8Array
  /** Home station of the new agent, −1 = none. */
  home: Int32Array
  yard: Int32Array
  /** Next order in the yard's queue, −1 = last. */
  next: Int32Array
  price: Float64Array
  /**
   * Balance pass T4 (pay on delivery, D-314 D1): the part of `price` the buyer still owes, booked when the ship is
   * delivered (0 = prepaid at order time, the M4 rule and every player order).
   */
  due: Float64Array
  /** Balance pass T4: 1 = a fracture-rig replacement miner (rigged when built, queued ahead of the other orders). */
  rig: Uint8Array
  placedAt: Float64Array
  /** −1 until the build starts. */
  startedAt: Float64Array
  /** Completion time, −1 until the build starts. */
  eta: Float64Array
  /** Order id = `ord-<seq>`. */
  seq: Uint32Array
}

/** Construction sites (spec §6.5). Per-ware arrays are `site × WARE_COUNT + ware`. */
export interface SiteStore {
  capacity: number
  count: number
  high: number
  free: Int32Array
  freeTop: number
  alive: Uint8Array
  /** SITE_STAGE. */
  stage: Uint8Array
  template: Uint8Array
  level: Uint8Array
  owner: Uint8Array
  sys: Uint16Array
  /** The node reserved for the future station (NODE_KIND.station, ref −1 until built), −1 = not yet created. */
  node: Int32Array
  x: Float64Array
  y: Float64Array
  z: Float64Array
  required: Float32Array
  delivered: Float32Array
  buildDays: Float32Array
  progressDays: Float32Array
  /** The faction day a `planned` site becomes `supplying`. */
  startDay: Int32Array
  /** The station it became, −1 = not yet. */
  station: Int32Array
  /** Site id = `site-<seq>`. */
  seq: Uint32Array
  /**
   * M8 (SNK-6a, D-TBD T30): `SITE_WORKS`: 1 when the site is a faction's surplus works (`ACTION_WORKS`), 2 when it is a
   * refit works (SNK-8: on its station's own node, completes without a change), 3 when it is a megaproject stage (LV
   * WK-1b: refit-shaped, constructor-fed, completes by stamping `charterStations.megaStage` only), else 0. Cleared by `createSite` and
   * `createUpgradeSite`; saved as the `systems.cols` column `sites.works` (v5).
   */
  works: Uint8Array
}

/** Off-screen wreck records (spec §8.3), at most `capacity`; the oldest are dropped first. */
export interface WreckStore {
  capacity: number
  count: number
  high: number
  free: Int32Array
  freeTop: number
  alive: Uint8Array
  sys: Uint16Array
  x: Float64Array
  y: Float64Array
  z: Float64Array
  /** M2 rolls the parts and their conditions from it. */
  seed: Uint32Array
  /** Hull index. */
  hull: Uint8Array
  cargoWare: Int8Array
  cargoQty: Float32Array
  createdAt: Float64Array
  expiresAt: Float64Array
  /** Wreck id = `ow-<seq>`. */
  seq: Uint32Array
  /**
   * M8 (MC-f5c): the faction index of the ship it came from when that faction is a maker (`WRECK_MAKER_IDS`), else
   * `WRECK_OWNER_NONE` (−1: raider, guardian, player or corporation). Saved as the record's `owner`, not as a column.
   */
  owner: Int8Array
  /** M9 (CH-1a): `WRECK_CLAIM` (0 none, 1 disabled, 2 open claim). Saved as the record's optional `claim`, not as a column. */
  claim: Uint8Array
}

/** Out-of-system encounters (spec §8.1-§8.2). Sides are agent-index lists of `sideCapacity`. */
export interface EncounterStore {
  capacity: number
  sideCapacity: number
  count: number
  free: Int32Array
  freeTop: number
  alive: Uint8Array
  /** ENCOUNTER_KIND. */
  kind: Uint8Array
  sys: Uint16Array
  node: Int32Array
  /** Seeded per encounter: `deriveSeed(seed, 'encounter', day, n)`. */
  seed: Uint32Array
  /** The encounter's own RNG state (mulberry32), so fine and coarse resolution draw the same numbers. */
  rngState: Uint32Array
  round: Uint8Array
  openedAt: Float64Array
  nextRoundAt: Float64Array
  attackers: Int32Array
  attackerCount: Uint8Array
  targets: Int32Array
  targetCount: Uint8Array
  /** Σ hp × strength of each side at the start (raiders disengage below 40%). */
  attackerStart: Float32Array
  targetStart: Float32Array
  /** Encounter serial. */
  seq: Uint32Array
}

/** Faction treasuries and today's ledger (spec §7.7). `day[f × LEDGER_COUNT + LEDGER.x]`. */
export interface FactionLedger {
  capacity: number
  /** Factions in use (factions.json count; M6 may add one at runtime). */
  count: number
  treasury: Float64Array
  /** Today's flows by category (positive = in). Reset by the day rollup. */
  day: Float64Array
}

/**
 * Balance round 2 (G3b): free repairs on dock, accumulated where `hp` is reset to 1 (`trader.ts` `dockFor`,
 * `raider.ts` `dockAtBase`). **Runtime-only, never saved** (zero after a load); the balance recorder takes deltas.
 * Index `faction × stride + role`.
 */
export interface RepairCounters {
  /** Row stride: `ROLE_IDS.length`. */
  stride: number
  /** Cumulative hp-equivalents repaired: Σ (1 − hp) at each reset to 1. */
  hp: Float64Array
  /** Cumulative plating-equivalent t: Σ (1 − hp) × the hull's `buildCost.hull_plating` (at f = 1; a sink multiplies by f). */
  platingT: Float64Array
  /** Per hull: `buildCost.hull_plating`, t (0 = none). */
  platingPerHull: Float64Array
}

/**
 * M8 demand sinks (plan 2026-10-04 SNK-1, D-TBD T24): what every draw took, per `DRAW_SINK` category
 * (`sinks/draw.ts`). Runtime-only, never saved: allocated once with the state and zeroed on load. It is not the
 * M5 `SINK` table in `metrics.ts` (the loss-replacement share reads that one).
 */
export interface SinkLedger {
  /** `(sink × WARE_COUNT + ware) × 2 + own`: tonnes drawn; own = 1 when the payer owns the station (drawn free). */
  tonnes: Float64Array
  /** Per sink: credits the payers paid the stations (before tariffs). */
  money: Float64Array
  /** Per faction: works sites opened and completed (SNK-6a/6t write them). */
  worksOpened: Int32Array
  worksCompleted: Int32Array
  /** Per faction: faction days the works trigger fired but no listed kind had a target (SNK-6t; nothing opened, no cooldown). */
  worksBlocked: Int32Array
  /** SNK-1c, per `sink × ROLE_IDS.length + role`: tonnes drawn by agents of that role (`drawStock`; the pilot's draws are not in it). */
  roleTonnes: Float64Array
  /** SNK-1c, per `faction × WORKS_GATE_COUNT + gate` (`sinks/schema.ts` `WORKS_GATE`): faction days each works gate bound. */
  worksGate: Int32Array
  /** SNK-1c, per ware: tonnes delivered into works sites, by AI constructors (`agents/constructor.ts`) and refit draws (SNK-8c, `sinks/draw.ts`). */
  worksT: Float64Array
  /** SNK-8, per faction: refit works opened and completed (also counted in `worksOpened` / `worksCompleted`). */
  refitOpened: Int32Array
  refitCompleted: Int32Array
  /** SNK-8b, per `faction × 6 + gate` (caps, noTrigger, noTarget, reserve, treasury, offered): faction days each refit gate bound (report only). */
  refitGate: Int32Array
}

/** Per-system strategic values. `security` is `system × factionCapacity + faction`. */
export interface SystemStore {
  count: number
  factionCapacity: number
  /** 0..1 (spec §8.5). */
  danger: Float32Array
  /** Σ strength × hp of a faction's patrols in the system, refreshed each slow tick (spec §7.5.4). */
  security: Float32Array
  /** Raider groups holding in the system now (danger decay input). */
  raiderGroups: Uint8Array
  /** Value of cargo that passed through the system during the last full faction day, and today so far. */
  trafficValue: Float32Array
  trafficToday: Float32Array
  /** Today's production value (output at base prices), trade volume (credits), losses and loss value. */
  productionToday: Float32Array
  tradeToday: Float32Array
  lossesToday: Uint16Array
  lossValueToday: Float32Array
}

/** Relays as the core sees them (galaxy relay order). */
export interface RelayStore {
  count: number
  /** Endpoint system indices. */
  a: Uint16Array
  b: Uint16Array
  /** Relay node in system `a` and in system `b`. */
  nodeA: Int32Array
  nodeB: Int32Array
  online: Uint8Array
  long: Uint8Array
  /** Tonnes moved over the relay today and during the last full faction day. */
  flowToday: Float32Array
  flowLastDay: Float32Array
}

/** Offset + index neighbour lists: the systems within N jumps of `k` are `list[offset[k] .. offset[k + 1])`. */
export interface NeighbourList {
  offset: Int32Array
  list: Uint16Array
}

/** Routing tables over online relays (spec §7.3), filled by `world/routing.ts` `rebuildRouting`. */
export interface RoutingTables {
  systems: number
  /** `hops[a × systems + b]`, UNREACHABLE (255) when there is no online route. */
  hops: Uint8Array
  /** Next system on the route from a to b, −1 = none (or a = b). */
  next: Int16Array
  /** The relay node in system a that leads to system b (a and b adjacent), −1 otherwise. */
  exitNode: Int32Array
  /** Mean in-system crossing time per system, s. */
  crossS: Float32Array
  near1: NeighbourList
  near2: NeighbourList
  near3: NeighbourList
  /** Bumped on every rebuild. */
  version: number
}

/** Trader opportunity tables (spec §7.5.1), refreshed every 10 s fine and every coarse substep. `system × WARE_COUNT + ware`. */
export interface OpportunityTables {
  refreshedAt: number
  /** Best source per (system, ware): lowest B with at least minLot available; −1 = none. */
  srcStation: Int32Array
  srcUnit: Float32Array
  /** Best sink per (system, ware): highest projected S with at least minLot room; −1 = none. */
  sinkStation: Int32Array
  sinkUnit: Float32Array
  /** Best sink system per (source system, ware) within the trader range by S − travel penalty; −1 = none. */
  bestSinkSys: Int16Array
  bestSinkScore: Float32Array
}

/** The scheduler's binary min-heap on (t, agent) with lazy deletion by generation (spec §7.4). */
export interface EventHeap {
  capacity: number
  size: number
  t: Float64Array
  agent: Int32Array
  gen: Uint32Array
}

/** Hidden gate stability (spec §9). */
export interface GateState {
  stability: number
  extractedToday: number
  /** The part of `extractedToday` the player (and the player corporation) mined (M7 spec §10.1: counts × 1). */
  playerExtractedToday: number
  /**
   * The story clock runtime multiplier (M7 spec §10.1; default 1): AI extraction reaches the law × `gate.json
   * storyExtractionMult` × this, capped at `storyMultCap`. Saved; M7 derives it from `career.story` (`setStoryMult`).
   */
  storyMult: number
  /** Balance pass T2: the extraction EMA the law reads (`gate.json smoothDays`; t/day). Saved. */
  extractionEma: number
  /**
   * The player's (and the player corporation's) share of `extractionEma`, same smoothing (t/day; the law is linear,
   * so the split is exact). The AI floor (`gate.json aiFloor`, D-314 D2) lets only this share take an open gate
   * below `closedAt + 1`. Saved (optional; old saves load 0).
   */
  playerEma: number
  /** The offline catch-up floor (D-314 D3; `catchup.ts`), −1 when none. Transient: set and cleared inside a catch-up. */
  floor: number
  /** End-of-day stability, oldest first, at most strategic.json historyDays. */
  history: number[]
}

/** Id serials (the agent serial lives in `AgentPool.nextSeq`). */
export interface StrategicCounters {
  wreckSeq: number
  wireSeq: number
  siteSeq: number
  groupSeq: number
  orderSeq: number
  encounterSeq: number
}

/** Today's Reach-wide counts, folded into the day rollup and reset. */
export interface DayCounters {
  built: number
  lost: number
  convoysLost: number
  ordersPlaced: number
  /** Station indices completed today. */
  newStations: number[]
}

/** The local layer's pending changes (spec §10.1), per agent index. */
export interface LocalLayer {
  /** 1 = the agent's step changed since the last `LocalDelta`. */
  changed: Uint8Array
  /** 1 = the agent arrived in the local system since the last `LocalDelta`. */
  spawned: Uint8Array
}

/** Boundary lookups, built by `world/galaxyAdapter.ts` (stations added by seeding and construction). */
export interface WorldIndex {
  systemById: Map<string, number>
  relayById: Map<string, number>
  fieldById: Map<string, number>
  stationById: Map<string, number>
  /** The home (gate) system index, −1 until built. */
  home: number
  /** The home gate node, −1 until built. */
  gateNode: number
  /**
   * Per system: `nodes.countOfSystem` once the world and the seeded economy are built (`seedEconomy`), i.e. the
   * nodes a load replays. Nodes above it were added in play (construction sites) and are saved (D-TBD).
   */
  seedNodes: Uint16Array
  /**
   * How the world build read the galaxy's fields (MC-a4, D-TBD T20): 0 = `oreWeights` (M4–M7; every save before
   * v5), 1 = `mineralWeights ?? oreWeights` × grade. A new game takes it from galaxy.json `minerals.strategicGrades`;
   * a load takes the save's `mineralsVersion`, so old reserves never meet a new mix.
   */
  mineralsVersion: MineralsVersion
  /**
   * SUP-2b1 (D-751): the seeding revision the world was seeded at (economy-seed.json `revision` for a new game; a load
   * takes the save's `seedRevision`, absent = 0). `rollSystemMix` skips an anchor entry whose `since` is above it,
   * so an old save replays exactly the stations it was made with.
   */
  seedRevision: number
}

/** The field-mix version a world was built with (`WorldIndex.mineralsVersion`, saved as v5 `mineralsVersion`). */
export type MineralsVersion = 0 | 1

/**
 * One faction day's rollup (spec §5.5, §12, §13), built once per day by `rollup.ts` and kept for
 * strategic.json `rollupDays`. The Wire and the away report read it (never the event ring, which wraps).
 * Per station-ware arrays are `station × WARE_COUNT + ware` over `stationCount` stations.
 */
export interface DayRollup {
  day: number
  stationCount: number
  fillMin: Float32Array
  fillMax: Float32Array
  fillEnd: Float32Array
  /** The baseline after this day's update. */
  baseline: Float32Array
  /** The station's sell-side unit price S(fill) at day end (0 where it doesn't trade the ware). */
  unitEnd: Float32Array
  flowIn: Float32Array
  flowOut: Float32Array
  /** Per station. */
  starvedS: Float32Array
  /** Per system. */
  productionValue: Float32Array
  tradeVolume: Float32Array
  losses: Uint16Array
  lossValue: Float32Array
  danger: Float32Array
  /** Per relay, tonnes. */
  relayFlowT: Float32Array
  /** Per ware: mean unit price over stations trading it. */
  meanPrice: Float32Array
  /** Per faction: end-of-day treasury and the day's ledger (`f × LEDGER_COUNT + category`). */
  treasury: Float64Array
  ledger: Float64Array
  built: number
  lost: number
  convoysLost: number
  ordersPlaced: number
  newStations: number[]
  tetherstoneExtracted: number
  /** The part of `tetherstoneExtracted` the player (and the player corporation) mined; counts × 1 at the gate (M7 §10.1). */
  tetherstonePlayer: number
  stability: number
}

/**
 * 5(e) MC-e4 (RD6): the runtime reference behind the Export Terminal's floor on roles.json `convoy.importFloorWare`.
 * `deepMedian` is the median closing sell-side price (`DayRollup.unitEnd` > 0) of that ware over the deep-region
 * stations, taken from each new rollup (`rollDay` → `updateImportFloor`); the ware's base price before the first
 * faction day and when no deep station prices it. Not saved before v7: MC-e5a1 saves it as an optional field (absent =
 * the base price until the next faction day; v7 migration calls §5.3).
 */
export interface ImportFloorState {
  deepMedian: number
}

/** The whole strategic state: every store, the clock and the configuration (spec §3.2, §7.1). */
export interface StrategicState {
  seed: number
  galaxySeed: number
  /** Strategic seconds. */
  time: number
  /** Fine ticks run. */
  tick: number
  /** Strategic seconds advanced but not yet run as a whole fine tick (the clock driver's sub-tick carry). */
  carryS: number
  /** Faction day of `time` (floor(time / dayS)). */
  day: number
  /** Dev warp multiplier (×1..×120). */
  warp: number
  /** Dev commands allowed. */
  dev: boolean
  rng: Rng
  galaxy: Galaxy
  data: StrategicData
  dims: StateDims
  world: WorldIndex
  agents: AgentPool
  stations: StationStore
  lines: LineStore
  nodes: NodeTable
  fields: FieldStore
  groups: GroupStore
  orders: OrderStore
  sites: SiteStore
  wrecks: WreckStore
  encounters: EncounterStore
  factions: FactionLedger
  systems: SystemStore
  relays: RelayStore
  routing: RoutingTables
  opportunities: OpportunityTables
  heap: EventHeap
  /** Every event (wraps within a day at full traffic). */
  events: StrategicEventRing
  /** Every event except AI trades (keeps several days for cause attribution). */
  notable: StrategicEventRing
  gate: GateState
  counters: StrategicCounters
  today: DayCounters
  /** Balance round 2 (G3b): repairs on dock per faction × role. Runtime-only, never saved. */
  repairs: RepairCounters
  /** M8 demand sinks (SNK-1): tonnes and credits per draw category. Runtime-only, never saved. */
  sinks: SinkLedger
  /** The last `rollupDays` day rollups, oldest first. */
  rollups: DayRollup[]
  /** 5(e) MC-e4 (RD6): the import floor reference, recomputed once per faction day (`economy/exportTerminal.ts`). */
  importFloor: ImportFloorState
  /** Wire items, oldest first, at most strategic.json wireKeep. */
  wire: WireItem[]
  /** Commands queued for the start of the next fine tick, in arrival order (spec §3.6). */
  commands: StrategicCommand[]
  /** The player's system index, −1 = none (no agents frozen). */
  localSys: number
  local: LocalLayer
  // M5 stores (M5 plan §2, C0): allocated by allocateState, written only by the M5 slices (C11 wires them).
  /** Charter claims (M5 spec §5). */
  claims: ClaimStore
  /** System states and grievance (M5 spec §6). */
  sysStates: SystemStateStore
  /** Per-station M5 columns: disabling and guardian harassment (M5 spec §8.4, §9.2). */
  charterStations: StationCharterStore
  /** Faction pairs: pressure, relations, weariness and faction-to-faction standings (M5 spec §4.1, §8.1). */
  relations: RelationStore
  /** Faction AI: cooldowns and daily limits (M5 spec §7). */
  factionAi: FactionAiStore
  /** The player mirror: standings, bounties, crimes (M5 spec §12, S-M2-13). */
  player: PlayerCharterStore
  /** Combat zones (M5 spec §8.3). */
  zones: ZoneStore
  /** Raider clans (M5 spec §9.1). */
  clans: ClanStore
  /** The guardian cell (M5 spec §9.2; hidden). */
  guardianCell: GuardianCellState
  /** Charter votes (M5 spec §11). */
  votes: VoteStore
  /** Per ware: a LEGALITY code (M5 spec §11 `illegal[ware]`). */
  legality: Uint8Array
  /** Gate stages and the Isolated Era (M5 spec §10). */
  gateStage: GateStageState
  /** M7 story tier 0..5 (S-M7-1; the Career sends it; default 0). */
  storyTier: number
  /** M7 world flags (M5 spec §25). Never read in hot paths; only `authority_exposed` has behaviour (§11). */
  flags: Map<string, number | string | boolean>
  /** Faction requests for M2's contract board (M5 spec §7.3). */
  requests: RequestStore
  // M6 stores (plan §2, P0): allocated by P1 in allocateState, written only by the M6 slices and commands (P15 wires them).
  /** Player ships: order, params, captain, insurance, earnings (M6 spec §4, §6.1). */
  fleet: FleetStore
  /** Hired captains and the station rosters (M6 spec §5). */
  captains: CaptainStore
  /** The player corporation (M6 spec §7.1); a sole trader until founding. */
  corp: CorpRecord
  /** Player station charters and commissions (M6 spec §7.2). */
  commissions: CommissionStore
  /** The dead captains, a capped ring (M6 spec §5; captains.json memorial). */
  memorial: MemorialRing
  /** Captain log lines, a capped ring (M6 spec §5, §8; plan P13). */
  feed: FleetFeed
  /** Fleet alerts queued for the next `AdvanceResult.fleetAlerts` (plan P10, P15). */
  pendingAlerts: FleetAlert[]
  // M7 store (plan §2, S16): allocated by allocateState; written only by the story commands and the story day slice.
  /** The worker's story state (online days, grievances, floors, ballots, retaliation, reactivation records). */
  story: StoryStore
}

// ---------------------------------------------------------------------------------------------
// Station sites (spec §4.4; seam S-M3-5)
// ---------------------------------------------------------------------------------------------

export interface ExternalStationSite {
  id: string
  name: string
  /** M3 station type (mapped to a template through templates.json `typeMap`). */
  type: string
  faction: FactionId
  position: [number, number, number]
  anchor: { kind: 'body' | 'field' | 'cluster' | 'free'; id: string | null }
}

export interface StationSiteSource {
  sites(galaxy: Galaxy, systemIndex: number): readonly ExternalStationSite[]
}

// ---------------------------------------------------------------------------------------------
// Protocol: the local handoff (spec §10.1)
// ---------------------------------------------------------------------------------------------

export interface NodeRef {
  id: string
  kind: NodeKindName
  pos: [number, number, number]
}

export type LocalStep =
  | { kind: 'docked'; station: string; until: number }
  | { kind: 'leg'; from: NodeRef; to: NodeRef; t0: number; t1: number; purpose: LegPurposeName }
  | { kind: 'mining'; field: string; until: number }
  | { kind: 'holding'; node: NodeRef; until: number }

export interface WareLot {
  ware: WareId
  qty: number
}

export interface LocalAgent {
  id: string
  role: RoleId
  faction: FactionId
  teamId: number
  hull: string
  shipDef: string
  hp: number
  cargo: WareLot | null
  groupId: string | null
  step: LocalStep
  /**
   * M6 (plan P16): present for a player ship. `faction` then holds a data faction (`'mutual'`, a placeholder the
   * FactionId-typed local stores accept) and `teamId` the player slot's team; read ownership from this block.
   */
  fleet?: LocalFleet
  /** RR-F1 BL-2b (D-803 C4, D-828 BL-T (c)): what an AI miner, trader or supplier is doing, for its contact label. Absent = none. */
  job?: LocalJob
}

/**
 * RR-F1 BL-2b (D-803 C4, D-828 BL-T (c)): an AI hauler's purpose, ids only (the client builds the label text once
 * per step change). Presentation only: in no hash and no save. `carry`: `ware` bound for station `to` (a booked
 * or laden miner, a laden trader or supplier); `load`: flying empty to load `ware` at station `to`; `standby`:
 * standing by at station `to` (`ware` null; BL-1a).
 */
export interface LocalJob {
  kind: 'carry' | 'load' | 'standby'
  ware: WareId | null
  to: string
}

/** A player ship's standing-order and crew picture for the local layer (M6 plan P16; the wing reads it, P22/P25). */
export interface LocalFleet {
  /** Fleet ship id (`ps-<seq>`) and name. */
  shipId: string
  name: string
  /** The captain (`cap-<seq>`), null = none; their skills by SKILL (1..5), all 0 without a captain. */
  captainId: string | null
  skills: number[]
  order: OrderName
  roe: RoeName
  livery: LiveryColors
  /** M6 (plan P25): true when the order is Escort(the player); the wing (src/wing) recruits these. Absent = false. */
  escortPlayer?: true
}

export interface LocalManifest {
  systemId: string
  time: number
  stations: StationView[]
  agents: LocalAgent[]
  wrecks: OffscreenWreck[]
  /** M5 (spec §14.3): the system's combat zones, and which manifest agents fight in them or lurk as war bands. */
  zones: LocalZone[]
  warBands: string[]
}

/** A combat zone in the local system (M5 spec §14.3): the two sides' zone agents fight for real there. */
export interface LocalZone {
  id: string
  sideA: FactionId
  sideB: FactionId
  node: NodeRef
  until: number
  /** Agent ids of each side's zone ships. */
  agentsA: string[]
  agentsB: string[]
}

export interface LocalSync {
  id: string
  hp: number
  cargoQty: number
  /** 0..1 along the current step. */
  progress: number
}

export interface LocalDelta {
  spawns: LocalAgent[]
  /** BL-2b: `job` as in `LocalAgent.job` (absent = none: the client clears the label). */
  steps: { id: string; step: LocalStep; job?: LocalJob }[]
}

// ---------------------------------------------------------------------------------------------
// Protocol: views (spec §5.6, §11.2, §12.3)
// ---------------------------------------------------------------------------------------------

export type PriceCauseTag = 'supplyShock' | 'convoyLost' | 'raid' | 'inputShortage' | 'demand' | 'dump' | 'oversupply'

export interface PriceCause {
  tag: PriceCauseTag
  /** Signed percent vs the baseline price (+24 = 24% above). */
  deltaPct: number
  text: string
}

export interface MarketRow {
  ware: WareId
  buys: boolean
  sells: boolean
  stock: number
  capacity: number
  fill: number
  /** What the station pays the player per t (null if it doesn't buy). */
  sellUnit: number | null
  /** What the player pays per t (null if it doesn't sell). */
  buyUnit: number | null
  /**
   * 5(e) MC-e4 (RD6): on a floored import row only (the Export Terminal's alloy), the floor per t and the reserved
   * tonnes, so the main thread quotes `max(curve, floorUnit × q)` exactly (`buyUnit` already shows the floored price).
   */
  floorUnit?: number
  reserved?: number
  /** vs the 3-day baseline. */
  trend: -1 | 0 | 1
  cause: PriceCause | null
}

export interface LineView {
  recipe: string
  status: LineStatusName
  missing: WareId | null
  /** 0..1 through the current cycle. */
  progress: number
}

export interface StationView {
  id: string
  name: string
  systemId: string
  template: string
  assetId: string
  owner: CorpFactionId
  pos: [number, number, number]
  dockable: boolean
  wares: MarketRow[]
  lines: LineView[]
  workforceMult: number
  habitat: { satisfaction: number } | null
  shipyard: { queue: { hull: string; faction: FactionId; eta: number | null }[] } | null
  /** Stations under construction are sites (spec §6.5). */
  construction: null
  /** M5 (spec §15.5): the system's claim holder and tariff fraction (the owner line), the disabling faction, the state banner. */
  holder: CorpFactionId | null
  tariff: number
  disabledBy: CorpFactionId | null
  banner: StationBanner | null
  /**
   * RR-F1 BL-2a (D-803 C4, D-828 BL-T (b)): the ships waiting on this station, counted by `stationViewsIn` (message
   * rate): stood-down AI miners docked here, AI miners booked to sell here (`incoming` > 0, `dst` = here) and idle-empty
   * AI traders and suppliers standing by here. Presentation only (in no hash, no save); absent when all three are 0.
   */
  miners?: { stoodDown: number; booked: number; haulersWaiting: number }
  /**
   * LV WK-1v (plan 2026-10-13 D5): the faction megaproject at this station, `stage` of `of` stages built (`name`, e.g.
   * "Kestrel Ore Sling", the seeded name since D-1121 card A2). Set by `buildStationView` only while
   * `works.megaproject` is configured and `stage` > 0; absent otherwise (keys off). Presentation only (in no hash, no save).
   */
  megaStage?: MegaStageView | null
  /**
   * D-1128 (megaproject recs §6 step 3): the founded megaproject's form, for the in-game grammar placeholder: the stored
   * kind and maker (founding.ts) and the form seed `deriveSeed(galaxySeed, 'megaproject', station, 'form')`. Set only
   * while the station holds a megaproject with a stored kind and maker; absent otherwise (keys off). Presentation only.
   */
  megaForm?: MegaFormView
}

/** D-1128: a founded megaproject's form as views carry it (kind and maker ids, form seed). Presentation only. */
export interface MegaFormView {
  kind: string
  /** The maker's faction data id (`factions.json`). */
  maker: string
  seed: number
}

/**
 * LV WK-1v / D-1121 card A2: a station's megaproject as views show it: `stage` of `of` built, its `name`, and for a
 * captured one (maker ≠ owner) `builtBy` and `heldBy` (faction display names). Presentation only.
 */
export interface MegaStageView {
  stage: number
  of: number
  name: string
  builtBy?: string
  heldBy?: string
}

/** The station screen's state banner (M5 spec §15.5): a disabled station, else the system's weightiest state. */
export interface StationBanner {
  kind: StateKind | 'disabled'
  stage: 'pending' | 'active' | 'recovering'
  text: string
}

export interface SiteView {
  id: string
  systemId: string
  template: string
  level: number
  owner: CorpFactionId
  pos: [number, number, number]
  stage: SiteStageName
  required: Partial<Record<WareId, number>>
  delivered: Partial<Record<WareId, number>>
  buildDays: number
  progressDays: number
  /**
   * LV WK-1v: the site's works kind when it is a megaproject stage (`SITE_WORKS.megaproject`), with the anchor station
   * it builds beside (`stationId`); absent for every other site.
   */
  works?: 'megaproject'
  stationId?: string
}

export interface SystemEconomyView {
  systemId: string
  stations: StationView[]
  sites: SiteView[]
  danger: number
  agentsByRole: Record<RoleId, number>
  fields: { id: string; reserves: Partial<Record<WareId, number>> }[]
  /** M5 (spec §16): the Charter claim, and the system's active combat zones. */
  claim: ClaimView
  zones: ZoneView[]
  /** M5 (spec §7.3, S-M2-12): the open faction requests issued in this system (the career bridge turns them into offers). */
  requests: FactionRequestView[]
  /** M5 (spec §11): the tetherstone ban is in force (carrying it is smuggling; the Charter's scans read it). */
  tetherstoneIllegal: boolean
}

/** An active combat zone (M5 spec §8.3, §16). */
export interface ZoneView {
  id: string
  sideA: FactionId
  sideB: FactionId
  node: NodeRef
  opened: number
  until: number
  /** Each side's strength now (its zone ships' Σ strength × hp plus the player's support). */
  strengthA: number
  strengthB: number
}

/** A system's claim and state summary on the galaxy view (M5 spec §15.3 overlays). */
export interface GalaxyCharterSummary {
  /** The holder; `'player'` for the corporation (D-424, §27.7), like `ClaimView.holder`. */
  holder: CorpFactionId | null
  contested: boolean
  tariff: number
  toll: number
  /** States not at stage none. */
  states: { kind: StateKind; stage: 'pending' | 'active' | 'recovering' }[]
  zones: number
}

export interface GalaxySystemEconomy {
  id: string
  worstShortage: { ware: WareId; fill: number } | null
  topSurplus: { ware: WareId; fill: number } | null
  danger: number
  productionValue: number
  tradeVolume: number
  agents: number
  /** WARE_COUNT mean unit prices (0 where nobody trades the ware). */
  prices: Float32Array
  /** M5: the claim and state summary (always set by `buildGalaxyView`; optional so M4-only fixtures stay valid). */
  charter?: GalaxyCharterSummary
  /**
   * LV WK-1v: the system's furthest-built megaproject (its anchor station's `StationView.megaStage`, the highest stage
   * in the system), for the galaxy map's badge; absent with no stage built or the keys off, so the old shape holds.
   */
  megaStage?: { stage: number; of: number; name: string }
}

export interface GalaxyEconomyView {
  systems: GalaxySystemEconomy[]
  /** `flowT`: tonnes moved over the relay in the last faction day. */
  relays: { id: string; online: boolean; flowT: number }[]
  /** M5 war fronts: relays between systems held by factions at war (always set by `buildGalaxyView`). */
  fronts?: { relayId: string; a: FactionId; b: FactionId }[]
  /**
   * M8 SNK-6c: one row per open works request (SNK-6b1), in request-list order: the galaxy map's Works overlay. Always
   * set by `buildGalaxyView` (empty with sinks.json `works.enabled` off); optional so M4-M7 fixtures stay valid.
   */
  works?: GalaxyWorksRequest[]
}

/** M8 SNK-6c: an open works request's galaxy summary (its site still needs the ware). */
export interface GalaxyWorksRequest {
  systemId: string
  /** The works site, `site-<seq>`. */
  siteId: string
  faction: FactionId
  /** The request's ware (the site's largest missing ware when it opened). */
  ware: WareId
  /** Tonnes of `ware` the site still misses now. */
  missingT: number
  /** Today's premium on the request's unit (`worksPremium`). */
  premium: number
  /** Whole faction days since the request opened. */
  daysOpen: number
}

// ---------------------------------------------------------------------------------------------
// Protocol: the Reach Wire and the away report (spec §12.1-§12.2, §13)
// ---------------------------------------------------------------------------------------------

/** Wire item kinds. `notice` is an injected item (M5/M7 `postWire`). */
export type WireKind =
  | 'shortage'
  | 'surplus'
  | 'priceShock'
  | 'stationBuilt'
  | 'shipsBuilt'
  | 'fieldDepleted'
  | 'convoyLost'
  | 'raid'
  | 'notice'
export const WIRE_KINDS = [
  'shortage',
  'surplus',
  'priceShock',
  'stationBuilt',
  'shipsBuilt',
  'fieldDepleted',
  'convoyLost',
  'raid',
  'notice',
] as const satisfies readonly WireKind[]

export interface WireCause {
  kind: CauseKindName
  /** The rendered cause phrase. */
  text: string
}

export interface WireItem {
  /** From `counters.wireSeq`, increasing. */
  id: number
  t: number
  day: number
  kind: WireKind
  severity: 1 | 2 | 3
  systemId: string
  stationId?: string
  /** The ware the item is about (the "My cargo" filter). */
  ware?: WareId
  factions: FactionId[]
  headline: string
  cause?: WireCause
  /** M7 (spec §12.3, S17 writes it): the outlet that ran the item; absent = the neutral Wire. */
  source?: StoryWireOutlet
}

export interface AwayReport {
  awaySeconds: number
  simulatedDays: number
  capped: boolean
  /** The top 8 by severity and relevance. */
  headlines: WireItem[]
  /** Wares in the hold, and at the last docked station. */
  markets: { ware: WareId; stationId: string; fromUnit: number; toUnit: number }[]
  traffic: { built: number; lost: number; convoysLost: number }
  newStations: { id: string; name: string; systemId: string }[]
  /** M6 (plan P13 fills it): the fleet lines, newest first. */
  fleet: AwayFleetLine[]
  /** M6 (plan P13): the fleet totals over the absence; absent before the first ship. */
  fleetSummary?: AwayFleetSummary
  /** M7 (S26): story warnings; absent when the absence touched no story term. */
  story?: AwayStoryReport
}

/** M7 (S26): a story floor the absence held past its end (the player must be online for it to run out, spec §8.2). */
export type AwayFloorLine =
  | { kind: 'grievance'; a: StoryParty; b: StoryParty; floor: number; untilOnlineDay: number }
  | { kind: 'aggression'; faction: FactionId; floor: number; untilOnlineDay: number }

/** M7 (S26): the away report's story section. */
export interface AwayStoryReport {
  /** Floors extended because they would have ended offline (the worker's `floorExtended`). */
  floorsExtended: AwayFloorLine[]
  /** The main thread's countdown warnings (`catchUpStory`; the story bridge adds them, `withStoryWarnings`). */
  warnings: string[]
}

// ---------------------------------------------------------------------------------------------
// Protocol: commands and results (spec §11.3, §5.4)
// ---------------------------------------------------------------------------------------------

export type TradeSide = 'buy' | 'sell'
export type TradeError = 'priceMoved' | 'noStock' | 'noRoom' | 'notTraded' | 'unknownStation'
export type Killer = 'player' | FactionId | null

export interface TradeQuote {
  qty: number
  unit: number
  total: number
  fillBefore: number
  fillAfter: number
  /** True when `qty` was clamped to what fits. */
  clamped: boolean
}

export type TradeResult = { ok: true; qty: number; total: number; unit: number } | { ok: false; error: TradeError; quote?: TradeQuote }

export type DevCommand =
  /** Empties a traded ware's stock once (a market shortage): a running cycle still finishes; `starveLine` stops a line. */
  | { type: 'dev'; op: 'forceShortage'; stationId: string; ware: WareId }
  | { type: 'dev'; op: 'spawnRaid'; systemId: string }
  | { type: 'dev'; op: 'loseNextConvoy' }
  | { type: 'dev'; op: 'runDays'; days: number }
  | { type: 'dev'; op: 'setStability'; value: number }
  /**
   * Playtest FC-R2-2: opens a works site (and its works request) in `systemId`, default the first system that takes
   * one, nearest the home system first (`factions/actions.ts devForceWorks`). `createdId` is the site's `site-<seq>`.
   */
  | { type: 'dev'; op: 'forceWorks'; systemId?: string }
  /**
   * Playtest FC-R2-3: starves station `stationId`'s line `line` (index in the station, default its first line with
   * a bought input) of a bought input, so a sale of it restarts the line (`economy/production.ts devStarveLine`).
   */
  | { type: 'dev'; op: 'starveLine'; stationId: string; line?: number }
  /** Playtest R3-H3: posts a shortage Wire item of `ware` at `stationId` now (`wire.ts devPostShortage`); `createdId` = its id. */
  | { type: 'dev'; op: 'postShortage'; stationId: string; ware: WareId }
export type DevOp = DevCommand['op']

/** Playtest dev op refusals: `forceWorks` (works off, no target) and `starveLine` (no such line; or `notTraded`). */
export type DevOpError = 'worksOff' | 'noWorksTarget' | 'noLine'

export type StrategicCommand =
  | { type: 'playerTrade'; stationId: string; ware: WareId; qty: number; side: TradeSide; expectedUnit: number; standing: number }
  | { type: 'playerMined'; systemId: string; fieldId: string; ware: WareId; qty: number }
  | { type: 'playerSoldComponent'; stationId: string; component: ComponentType; condition: number }
  | { type: 'localStepComplete'; agentId: string }
  | { type: 'localReleased'; agentId: string }
  /** M7 (S1, D-339): `selfDefence` = the victim fired on the player first (a guardian kill then costs no standing). */
  | { type: 'localDestroyed'; agentId: string; killer: Killer; selfDefence?: boolean }
  | { type: 'setRelayOnline'; relayId: string; online: boolean }
  | { type: 'setWarp'; mult: number }
  | DevCommand
  /** M5 (spec §16, C11): the Charter commands. */
  | CharterCommand
  /** M6 (spec §4-§7, P15): the fleet and corporation commands. */
  | M6Command
  /** M7 (plan §2, S16): the story commands (`story/index.ts` routes them). */
  | M7Command
  /** M8 (plan §4.5, SNK-3a): the pilot's sink draw (`economy/market.ts`). */
  | PlayerDrawCommand
  /** M8 (plan §4.5, SNK-6b1): the pilot's hand-in to a works site (`factions/requests.ts worksDeliver`). */
  | WorksDeliverCommand
  /** M9 (plan CH-1b): the player opens or abandons a claim on a disabled off-screen wreck (`combat/wrecks.ts markClaim`). */
  | MarkClaimCommand
  /** M9 (plan CH-7a): register, fix and scrap a claimed ship (`fleet/claims.ts`). */
  | ClaimedShipCommand
  /** M10 P5a10w/P5a11a (D-1073): the cradle pair's player commands (`fleet/carrier` transitions in commands.ts). */
  | CarrierCommand
export type StrategicCommandType = StrategicCommand['type']

export type CommandError =
  | TradeError
  | 'unknownSystem'
  | 'unknownField'
  | 'unknownAgent'
  | 'unknownRelay'
  | 'notLocal'
  | 'notComponentBuyer'
  | 'devDisabled'
  | 'invalid'
  | CharterCommandError
  /** M6 (plan P15 routes the M6 commands). */
  | M6CommandError
  /** M7 (plan S16 routes the M7 commands). */
  | M7CommandError
  /** M9 (plan CH-1b): `markClaim` refusals. */
  | MarkClaimError
  /** M9 (plan CH-7a): claimed-ship refusals. */
  | ClaimedShipError
  /** M8 (plan SNK-6b1): `worksDeliver` refusals. */
  | WorksDeliverError
  /** Playtest dev ops (`forceWorks`, `starveLine`). */
  | DevOpError

/** Normal refusals are typed errors and never throw (the M2 rule). */
export type CommandResult =
  | {
      ok: true
      /** playerTrade: what executed. */
      trade?: { qty: number; total: number; unit: number }
      /** playerSoldComponent: the ware yield deposited. */
      deposited?: Partial<Record<WareId, number>>
      /** M5 payFines: the amount the Career pays. */
      amount?: number
      /** M5: standing deltas the command produced (the Career applies them). */
      standingDeltas?: StandingDelta[]
      /** M6: credits for the Career to add to (+) or take from (−) the wallet now (see `AdvanceResult.walletDelta`). */
      walletDelta?: number
      /** M6: the boundary id the command created (a ship `ps-<seq>`, a captain `cap-<seq>`, a commission `pst-<seq>`). */
      createdId?: string
      /** M8 worksDeliver (SNK-6b1): what the site took and what the owner's treasury paid for it. */
      works?: WorksDeliverOutcome
      /** Dev starveLine (FC-R2-3): the line stopped (index in the station) and the input it now lacks. */
      starved?: { line: number; ware: WareId }
    }
  | { ok: false; error: CommandError; quote?: TradeQuote }

// ---------------------------------------------------------------------------------------------
// Protocol: the host (spec §11.1)
// ---------------------------------------------------------------------------------------------

/** Which geography and station-site source the host builds with (plan E12; I0 registers 'm3'). */
export type SourceId = 'fallback' | 'm3'

export interface InitOptions {
  galaxySeed: number
  seed: number
  save?: StrategicSave
  /** Enables dev commands. */
  dev?: boolean
  sources?: SourceId
}

export interface InitResult {
  time: number
  day: number
  galaxySeed: number
  seed: number
  stations: number
  agents: number
  /** True when the state came from a save. */
  fromSave: boolean
}

export interface AdvanceResult {
  clock: { time: number; day: number; dayFraction: number; warp: number }
  delta: LocalDelta | null
  /** Newest Wire item id (−1 = none yet). */
  wireHead: number
  /** `fleet` (M6, P16): the fleet, captain, corp, player-station or feed views changed since last built. Absent = false. */
  dirty: { system: boolean; galaxy: boolean; fleet?: boolean }
  /** M5 (S-M2-13): standing deltas for the Career to apply (the Career is the source of truth). Absent = none. */
  standingDeltas?: StandingDelta[]
  /** Guardian ships the player destroyed since the last advance (M7 §12.1 logging), for the Career. Absent = none. */
  guardianKills?: GuardianKill[]
  /** M5: present when relations changed since the last advance (M5 spec §14.1). */
  relations?: RelationsView
  /** M5: present when the gate stage changed since the last advance (M5 spec §10). */
  gate?: GateView
  /**
   * M6 (plan §2, P15): credits for the Career to add to (+) or take from (−) the personal wallet: the sole trader's
   * day net, company → wallet transfers (+), the founding fee and wallet → company transfers (−). Absent = 0.
   */
  walletDelta?: number
  /** M6 (plan P15): fleet alerts raised since the last advance (asset under attack, losses, deaths). Absent = none. */
  fleetAlerts?: FleetAlert[]
  /**
   * M7 (plan §2, S16): online faction days that ended since the last advance (the Career counts
   * `career.story.onlineDays` from it; a catch-up day is not online, so it never counts here). Absent = 0.
   */
  onlineDays?: number
  /**
   * M7 S43: what the worker did to reactivation records that the Career mirrors (titles converted or released at
   * founding, restarts reverted, petty-registry refusals), in order. Absent = none.
   */
  storyReactivations?: StoryReactivationNote[]
}

export interface StrategicStats {
  time: number
  day: number
  tick: number
  warp: number
  agents: number
  agentsByRole: Record<RoleId, number>
  agentsByState: Record<AgentStateName, number>
  stations: number
  groups: number
  orders: number
  wrecks: number
  heapSize: number
  /** Wall-clock fine-tick timing from the injected `now`, ms. */
  tickMsP50: number
  tickMsP95: number
  tickMsMax: number
  eventsPerSecond: number
  moneySupply: number
  /** Hidden gate value (dev panel only). */
  stability: number
}

/** What the worker exposes; the in-process fallback is the same object (spec §11.1). */
export interface StrategicHost {
  init(opts: InitOptions): Promise<InitResult>
  /** Runs whole fine ticks (real seconds × warp); returns deltas. */
  advance(realSeconds: number): Promise<AdvanceResult>
  command(cmd: StrategicCommand): Promise<CommandResult>
  enterSystem(systemId: string): Promise<LocalManifest>
  exitSystem(): Promise<void>
  syncLocal(batch: readonly LocalSync[]): Promise<void>
  systemView(systemId: string): Promise<SystemEconomyView>
  stationView(stationId: string): Promise<StationView>
  galaxyView(): Promise<GalaxyEconomyView>
  wireSince(afterId: number): Promise<WireItem[]>
  /** `onProgress` crosses the worker boundary as a `Comlink.proxy`. */
  /** `player` (M6): the Career's docked station, hold and wallet, so the away report and the sole-trader hold see them. */
  catchUp(seconds: number, onProgress?: (day: number, days: number) => void, player?: CatchUpPlayer): Promise<AwayReport>
  save(): Promise<StrategicSave>
  stats(): Promise<StrategicStats>
}

// ---------------------------------------------------------------------------------------------
// Save records (spec §14; typed-array columns are base64 strings)
// ---------------------------------------------------------------------------------------------

export interface ShipOrder {
  id: string
  faction: FactionId
  hull: string
  role: RoleId
  /** Home station id, null = none. */
  home: string | null
  yard: string
  price: number
  /** Balance pass T4: the price still owed on delivery (0 = prepaid; absent in older saves). */
  due: number
  /** Balance pass T4: a fracture-rig replacement miner (false in older saves). */
  rig: boolean
  placedAt: number
  startedAt: number | null
  eta: number | null
}

export interface ConstructionSite {
  id: string
  systemId: string
  template: string
  level: number
  owner: FactionId
  position: [number, number, number]
  required: Partial<Record<WareId, number>>
  delivered: Partial<Record<WareId, number>>
  stage: SiteStageName
  buildDays: number
  progressDays: number
  startDay: number
}

/** Named so it never clashes with M2's world-delta `WreckRecord` (spec §8.3). */
export interface OffscreenWreck {
  /** `ow-<seq>`. */
  id: string
  systemId: string
  /** System coordinates, float64 m (the encounter node plus a seeded offset ≤ 5 km). */
  pos: [number, number, number]
  /** M2 rolls the parts and their conditions from it. */
  seed: number
  /** hulls.json id → its local ship def → M2's salvage layout. */
  hull: string
  /** Goes into the layout's cargo pod. */
  cargo: WareLot | null
  /** Strategic seconds; lifetime 5 faction days. */
  createdAt: number
  expiresAt: number
  /**
   * M8 (MC-f5c; save v5): the faction index of the ship it came from when that faction is a maker, else −1
   * (`WRECK_OWNER_NONE`: raider, guardian, player or corporation). Absent in v1–v4 records (`migrateV4toV5` writes −1).
   */
  owner?: number
  /** M8 (MC-f5c): who made its parts (from `owner`), set only in the local manifest's records and only when there is one. */
  maker?: WreckMakerId
  /** M9 (CH-1a; save v6): 1 = a disabled, claimable hull; 2 = an open claim. Absent = 0 (an ordinary wreck; every v1–v5 record). */
  claim?: WreckClaim
}

/** A v5 save's wreck record (MC-f5c): `owner` is always written. */
export interface SavedOffscreenWreck extends OffscreenWreck {
  owner: number
}

export interface GroupRecord {
  /** `grp-<seq>`. */
  id: string
  kind: GroupKindName
  state: number
  faction: FactionId
  /** Agent ids. */
  leader: string
  members: string[]
  target: number
  home: string | null
  timer: number
}

/** A day rollup in the save: scalar fields plus base64 columns (the in-memory `DayRollup` arrays). */
export interface DayRollupRecord {
  day: number
  built: number
  lost: number
  convoysLost: number
  ordersPlaced: number
  newStations: string[]
  tetherstoneExtracted: number
  tetherstonePlayer?: number
  stability: number
  cols: Record<string, string>
}

export interface StrategicSaveV1 {
  version: 1
  galaxySeed: number
  seed: number
  time: number
  tick: number
  rngState: number
  counters: StrategicCounters & { agentSeq: number }
  /** Base64 bitset by relay index. */
  relaysOnline: string
  stations: {
    meta: { id: string; template: string; owner: FactionId; level: number; name: string; node: number; anchor?: 'asteroid' | 'ice' | 'gas' | 'other' }[]
    cols: Record<'stock' | 'reserved' | 'incoming' | 'baseline' | 'lineProgress' | 'habitat', string> & Record<string, string>
  }
  /** Every AgentPool column, trimmed to the high-water mark. */
  agents: { count: number; cols: Record<string, string> }
  /** Per-system columns (danger, traffic value). Additive to spec §14: danger must survive a reload. */
  systems: { cols: Record<string, string> }
  groups: GroupRecord[]
  orders: ShipOrder[]
  sites: ConstructionSite[]
  wrecks: OffscreenWreck[]
  /** Base64 reserves. */
  fields: string
  factions: { id: FactionId; treasury: number; ledger: number[] }[]
  gate: { stability: number; extractedToday: number; extractionEma?: number; playerEma?: number; playerExtractedToday?: number; storyMult?: number; history: number[] }
  /** The last 50. */
  wire: WireItem[]
  /** The last 10 days. */
  rollups: DayRollupRecord[]
}

/** The current save version (M5 adds v2, M6 v3, M7 v4, M8 v5, M9 v6, 5(e) v7 through `save.ts` migrations). */
export type StrategicSave = StrategicSaveV8

// =============================================================================================
// M5: factions and the Charter (M5 spec; plan C0). Append-only; frozen at the M5 fork commit so the
// M5 core waves build in parallel. Every store is allocated by `state.ts` and stays empty until C11
// wires the M5 day slices into `sim.ts`.
// =============================================================================================

/** System state kinds (M5 spec §6). `isolation` is global: its stage lives in `GateStageState`. */
export const STATE_KIND = { boom: 0, bust: 1, famine: 2, strike: 3, lockdown: 4, blockade: 5, war: 6, isolation: 7 } as const
export type StateKind = keyof typeof STATE_KIND
export const STATE_KIND_NAMES = ['boom', 'bust', 'famine', 'strike', 'lockdown', 'blockade', 'war', 'isolation'] as const satisfies readonly StateKind[]
export const STATE_KIND_COUNT = 8

/** A state's stage (M5 spec §6): pending (announced, 1 day) → active → recovering (half effect). */
export const STAGE = { none: 0, pending: 1, active: 2, recovering: 3 } as const
export type StageName = keyof typeof STAGE
export const STAGE_NAMES = ['none', 'pending', 'active', 'recovering'] as const satisfies readonly StageName[]

/** Relations between non-outlaw factions (M5 spec §8.1). */
export const RELATION = { peace: 0, economic: 1, skirmish: 2, war: 3 } as const
export type RelationName = keyof typeof RELATION
export const RELATION_NAMES = ['peace', 'economic', 'skirmish', 'war'] as const satisfies readonly RelationName[]

/** Crimes (M5 spec §12.2). */
export const CRIME = { assault: 0, murder: 1, piracy: 2, smuggling: 3, tollEvasion: 4, trespass: 5 } as const
export type CrimeKind = keyof typeof CRIME
export const CRIME_NAMES = ['assault', 'murder', 'piracy', 'smuggling', 'tollEvasion', 'trespass'] as const satisfies readonly CrimeKind[]

/** Faction AI actions (M5 spec §7.2). */
export const ACTION = { build: 0, expand: 1, press: 2, seekPeace: 3, relink: 4, sweep: 5, stoke: 6 } as const
export type ActionName = keyof typeof ACTION
export const ACTION_NAMES = ['build', 'expand', 'press', 'seekPeace', 'relink', 'sweep', 'stoke'] as const satisfies readonly ActionName[]
export const ACTION_COUNT = 7

/** Gate stages (M5 spec §10). */
export const GATE_STAGE = { normal: 0, flickers: 1, tremors: 2, closed: 3 } as const
export type GateStageName = keyof typeof GATE_STAGE
export const GATE_STAGE_NAMES = ['normal', 'flickers', 'tremors', 'closed'] as const satisfies readonly GateStageName[]

/** Personality traits (M5 spec §4.1): the column order of `StrategicData.charter.personality`. */
export const TRAIT = {
  order: 0,
  fees: 1,
  greed: 2,
  expansion: 3,
  tetherstone: 4,
  trade: 5,
  neutrality: 6,
  conflict: 7,
  survival: 8,
  labor: 9,
  gate: 10,
  opportunism: 11,
} as const
export type TraitName = keyof typeof TRAIT
export const TRAIT_NAMES = [
  'order',
  'fees',
  'greed',
  'expansion',
  'tetherstone',
  'trade',
  'neutrality',
  'conflict',
  'survival',
  'labor',
  'gate',
  'opportunism',
] as const satisfies readonly TraitName[]
export const TRAIT_COUNT = 12

/** Claim score components (M5 spec §5.2), the `cause` of `claimChanged`. */
export const CLAIM_COMPONENT = { ops: 0, sec: 1, inf: 2 } as const
export type ClaimComponentName = keyof typeof CLAIM_COMPONENT
export const CLAIM_COMPONENT_NAMES = ['ops', 'sec', 'inf'] as const satisfies readonly ClaimComponentName[]

/** Influence sources (M5 spec §5.1), the `source` argument of C1's `addInfluence`. */
export const INFLUENCE_SOURCE = {
  agentTrade: 0,
  restartDelivery: 1,
  relinkSponsor: 2,
  zoneWin: 3,
  playerContract: 4,
  playerTrade: 5,
  reputation: 6,
  isolation: 7,
} as const
export type InfluenceSourceName = keyof typeof INFLUENCE_SOURCE
export const INFLUENCE_SOURCE_NAMES = [
  'agentTrade',
  'restartDelivery',
  'relinkSponsor',
  'zoneWin',
  'playerContract',
  'playerTrade',
  'reputation',
  'isolation',
] as const satisfies readonly InfluenceSourceName[]

/** Toll hull classes (M5 spec §5.4): light 0, medium 150, large 500 (M6) credits. */
export const HULL_CLASS = { light: 0, medium: 1, large: 2 } as const
export type HullClassName = keyof typeof HULL_CLASS
export const HULL_CLASS_NAMES = ['light', 'medium', 'large'] as const satisfies readonly HullClassName[]

/** `legality[ware]` codes (M5 spec §11). */
export const LEGALITY = { legal: 0, illegal: 1 } as const

/** Charter vote measures (M5 spec §11), the `cause` of `voteScheduled` / `voteResolved`. */
export const MEASURE = { tetherstoneBan: 0, tetherstoneRepeal: 1 } as const
export type MeasureName = keyof typeof MEASURE
export const MEASURE_NAMES = ['tetherstoneBan', 'tetherstoneRepeal'] as const satisfies readonly MeasureName[]

/** Faction request kinds (M5 spec §7.3). */
export const REQUEST_KIND_NAMES = ['deliver', 'clearRaiders', 'zoneSupport'] as const
export type RequestKind = (typeof REQUEST_KIND_NAMES)[number]

/** Wanted levels (M5 spec §12.3). */
export type WantedLevel = 0 | 1 | 2 | 3

/** `sys` of an event that is Reach-wide (the global Isolation state, the gate). */
export const SYS_GLOBAL = 0xffff

/**
 * M5 treasury categories (M5 spec §4.2), appended after M4's `LEDGER` codes. C11 raises `LEDGER_COUNT` to
 * `LEDGER_COUNT_M5` together with the save v2 (the v1 ledger arrays have 8 entries).
 */
export const LEDGER_M5 = { tariffs: 8, tolls: 9, subsidy: 10, charterFees: 11, fines: 12, war: 13, hiddenTransfers: 14 } as const
export type LedgerM5Category = keyof typeof LEDGER_M5
export const LEDGER_COUNT_M5 = 15

/**
 * The faction day sliced over 16 fine ticks (M5 spec §3.2): the tick of the day each block of work runs on.
 * Faction AI runs one non-outlaw faction per tick from `factionAi` to `factionAiLast`, in faction order.
 * M4's six day-work items fold into `rollup`, `gate` and `jobs`. C11 wires this table into `sim.ts` and raises
 * strategic.json `clock.daySlices` to `count` (C0 keeps 6, so M4 behaviour is unchanged until then).
 * M6 (plan P0) appends `fleet` and `corp` and raises `count` and `clock.daySlices` to 18: slices 0-15 keep their
 * ticks (the sim maps slice k to work item k when the two counts match) and 16-17 run no work until P15 wires them.
 */
export const DAY_SLICE = {
  /** M4 rollup and baselines; M5 metric accumulation (ops, security, influence). */
  rollup: 0,
  claims: 1,
  states: 2,
  relations: 3,
  factionAi: 4,
  factionAiLast: 11,
  /** Law decay and votes. */
  law: 12,
  /** M4 gate stability, then the gate stages. */
  gate: 13,
  /** Raider clans and the guardian cell. */
  outlaws: 14,
  /** M4 jobs → orders (with M5 targets), sites, fields, danger, the Wire. */
  jobs: 15,
  /** M6 (plan P0; P15 wires it): wages, loyalty, captain rosters and recoveries. Empty until then. */
  fleet: 16,
  /** M6 (plan P0; P15 wires it): commissions, station policy, milestones and the rival response. Empty until then. */
  corp: 17,
  /** M7 (plan §2, S16): the online-day counter, then the story modules' day stubs (`story/day.ts`, S17–S24 fill them). */
  story: 18,
  count: 19,
} as const

/*
 * M5 event fields (unused fields as for M4: station/agent/aux −1, ware −1, faction/other FACTION_NONE, numbers 0):
 * - `claimChanged`: sys, faction = new holder (FACTION_NONE = unclaimed), other = previous holder, cause =
 *   CLAIM_COMPONENT with the largest swing, value = the new holder's S (0 when unclaimed), aux = the EV type of
 *   the biggest contributing event (0 = none), station = its station (−1), qty = 1 for a lapse, else 0.
 * - `claimContested`: sys, faction = challenger, other = holder, value = the lead.
 * - `stateStaged`: sys (SYS_GLOBAL for isolation), cause = STATE_KIND, qty = the new STAGE, value = days left.
 * - `relationChanged`: faction = a, other = b (a < b), cause = the new RELATION, aux = the previous one, value = pressure.
 * - `stationDisabled` / `stationCaptured`: sys, station, faction = the disabler / new owner, other = the owner / previous owner.
 * - `stationEnabled`: sys, station, faction = the owner, other = the former disabler.
 * - `zoneOpened` / `zoneClosed`: sys, faction = side A, other = side B, aux = zone seq; closed: cause = winner
 *   (0 = A, 1 = B, 2 = draw), value = the winner's surviving strength.
 * - `voteScheduled` / `voteResolved`: faction = sponsor (FACTION_PLAYER = the player), cause = MEASURE, aux = vote
 *   seq; resolved: qty = 1 passed / 0 failed, value = the yes weight.
 * - `gateStage`: sys = SYS_GLOBAL, cause = GATE_STAGE, aux = the previous stage, value = stability.
 * - `gateClosure`: sys = SYS_GLOBAL, qty = the closure length s, value = stability.
 * - `gateReopened`: sys = SYS_GLOBAL, value = stability.
 * - `crime`: sys, faction = jurisdiction (FACTION_NONE = lawless), other = victim faction, cause = CRIME, value =
 *   bounty added, qty = the standing delta.
 * - `bountyChanged`: faction, value = the new bounty, cause = the wanted level.
 * - `factionAction`: sys, faction, other = target faction (FACTION_NONE), cause = ACTION, aux = the target index
 *   (template, relay, clan), value = cost.
 * - `sweep`: sys, faction = the sweeper, aux = clan index, qty = 1 success / 0 failure.
 * - `guardianRaid`: sys, agent = target, faction = FACTION.guardian, other = the target's faction, ware, qty.
 * - `tetherstoneSeized`: sys, faction = the seizer (authority), other = the owner (tethys), ware, qty t, value.
 * - `varroStoke` (hidden): faction = a, other = b, value = pressure added.
 * - `holdfastFundsGuardians` (hidden): faction = holdfast, other = guardian, value = credits.
 */

/**
 * Charter claims (M5 spec §5). Per (system × faction) columns are `sys × factionCapacity + f`; the trend ring is
 * `(sys × factionCapacity + f) × trendDays + slot`. Outlaws never score.
 */
export interface ClaimStore {
  systems: number
  factionCapacity: number
  trendDays: number
  /** Next trend slot to write (0..trendDays − 1) and how many are filled. */
  trendHead: number
  trendFilled: number
  /** EMAs over 3 faction days (M5 spec §5.1). */
  ops: Float32Array
  sec: Float32Array
  inf: Float32Array
  /** Today's accumulators. */
  opsToday: Float32Array
  secSum: Float32Array
  secSamples: Uint16Array
  infToday: Float32Array
  /** S (0..100) from the last evaluation. */
  score: Float32Array
  /** The 10-day S ring. */
  trend: Float32Array
  /** Per system: the holder (−1 = unclaimed), the challenger (−1 = none), its lead streak and the holder's lapse streak (days). */
  holder: Int8Array
  challenger: Int8Array
  streak: Uint8Array
  lapseStreak: Uint8Array
  /** Per system: tariff fraction 0..0.1 and the medium-hull toll in credits (0 = no tolls). */
  tariff: Float32Array
  toll: Float32Array
  /** Per system: days the trade volume fell in a row (tariff −2%). */
  tradeFallStreak: Uint8Array
  /** Per system: the faction day of the last holder change (−1 = never). */
  changedDay: Int32Array
  /**
   * Per system: the last faction day the dev force-claim score seed holds the corporation's S at claims.json
   * `devForceClaim.seedScore` (−1 = none). Set only by the dev `forceClaim` command (saved, so a tester's reload keeps it;
   * older saves without it load as −1) and read only in dev builds (`state.dev`).
   */
  devSeedUntil: Int32Array
}

/** System states (M5 spec §6): `sys × STATE_KIND_COUNT + kind`, plus per-system effect outputs. */
export interface SystemStateStore {
  systems: number
  /** STAGE code. */
  stage: Uint8Array
  /** Days left in the current stage (−1 = while held). */
  daysLeft: Float32Array
  /** Consecutive days the trigger held (the "2 days running" rules). */
  triggerDays: Uint8Array
  /** Per system: Mutual grievance 0..100. */
  grievance: Float32Array
  /** Per-system effects written by C2's `applyStateEffects` (defaults 1, 1, 0, 1, 1, 0). */
  patrolMult: Float32Array
  legDelayMult: Float32Array
  dockExtraS: Float32Array
  workforceMult: Float32Array
  importMult: Float32Array
  interceptP: Float32Array
}

/** Per-station M5 columns (M5 spec §8.4, §9.2). */
export interface StationCharterStore {
  capacity: number
  /** The disabling faction (−1 = enabled). No station is ever destroyed. */
  disabledBy: Int8Array
  /** The faction building toward disabling it (−1), and the day streaks of the disable and re-enable rules. */
  disableCandidate: Int8Array
  disableStreak: Uint8Array
  enableStreak: Uint8Array
  /** Guardian harassment: output ×0.7 until this faction day (−1 = none). */
  harassUntilDay: Int32Array
  /**
   * M8 SNK-8: the faction day the station's last refit works completed (−1 = never; the refit cooldown and the only
   * thing a refit changes). Saved (v5) only once a refit has completed.
   */
  refitDay: Int32Array
  /**
   * LV WK-1b (D5): the megaproject stages completed at the station (0 = none; at most sinks.json
   * `works.megaproject.stages`). Adds nothing to the sim. Saved as an optional column only once a value is non-zero.
   */
  megaStage: Uint8Array
  /**
   * D-1121 card A2: the megaproject kind founded at the station, `MEGA_KIND_IDS` index + 1 (0 = none or unknown). Set
   * when the first stage opens (`selectMegaKind`) and fixed from then on: a capture keeps it (the captor only
   * repaints). Saved beside `megaStage` only for a station with a stage built or a live megaproject site.
   */
  megaKind: Uint8Array
  /** D-1121 card A2: the founding faction (the maker body), its faction index + 1 (0 = none); fixed like `megaKind`. */
  megaMaker: Uint8Array
}

/**
 * Faction pairs (M5 spec §4.1, §8.1), `a × factionCapacity + b`. `pressure`, `relation`, `weariness` and
 * `relationSince` are symmetric (writers keep both halves equal; `pairIndex` in state.ts gives the canonical
 * a < b slot). `standing`, `killsToday` and `warLossValue` are directional (a toward b / b's losses to a).
 */
export interface RelationStore {
  factionCapacity: number
  pressure: Float32Array
  /** RELATION code. */
  relation: Uint8Array
  weariness: Float32Array
  /** Faction day the current relation began. */
  relationSince: Int32Array
  /** Faction day the pair's last war ended (−1 = never; symmetric). `press.truceDays` reads it (R2-T4 part 3). */
  warEndDay: Int32Array
  /** a's standing toward b, −100..100 (factions.json starting standings). */
  standing: Float32Array
  /** Ships of b that a destroyed today (pressure +2 each, capped). */
  killsToday: Uint16Array
  /** Loss value a suffered from b during the current war (weariness). */
  warLossValue: Float64Array
}

/** Faction AI (M5 spec §7): per faction `f`; cooldowns per action at `f × ACTION_COUNT + a`. */
export interface FactionAiStore {
  factionCapacity: number
  /** The first faction day the action is allowed again. */
  cooldownUntil: Int32Array
  actionsToday: Uint8Array
  /** Treasury at seeding (the "below 20% of its start" pressure term). */
  startTreasury: Float64Array
  /** The relay of the faction's active relink (−1 = none) and when its reboot ends (strategic s). */
  relinkRelay: Int32Array
  relinkUntil: Float64Array
  /** Balance pass T7b: ships lost (`agentLost`) not yet re-ordered by job fill (−1 per order placed, floor 0). */
  unreplacedLosses: Float64Array
  /** Balance pass T7b part 2: the job-target ramp cap (Σ targets; 0 = none yet, `agents/jobs.ts` `rampTargetCap`). */
  targetCap: Float64Array
  /**
   * Balance round 2 R2-T2: stations each faction owns after `seedEconomy` (the growth cap's start). Derived, not
   * saved: the load path replays `seedEconomy`, which sets it again. 0 = unknown (no cap).
   */
  startStations: Int32Array
  /**
   * M8 surplus works (plan 2026-10-04 SNK-6a, D-TBD T30). `ACTION_WORKS` is outside M5's ACTION table, so its cooldown
   * is its own per-faction column, never a `cooldownUntil` slot: the first faction day `f` may open works again.
   */
  worksCooldownUntil: Int32Array
  /**
   * Per `f × WARE_COUNT + w`: consecutive faction days `f`'s own sellers of ware `w` sat at or above sinks.json
   * `works.surplusFill` (the trigger's streak; SNK-6t writes it). Saved (v5); a `WARE_COUNT` change re-lays it.
   */
  worksSurplusDays: Int16Array
  /** Per faction: the trigger's hysteresis bit (1 = in surplus until the fill drops below `works.stopFill`; SNK-6t). */
  worksSurplusOn: Uint8Array
  /**
   * Per `f × WORKS_OPEN_RING + slot`: the faction days `f` opened its recent works sites (−1 = empty), so sinks.json
   * `works.maxPer100d` holds across a save and load (SNK-6t). A new open overwrites the oldest slot. Saved (v5).
   */
  worksOpenDays: Int32Array
  /**
   * Per `f × REFIT_OPEN_RING + slot`: the faction days `f` opened its recent refit works (SNK-8; −1 = empty), so
   * sinks.json `works.refit.maxPer100d` holds across a save and load. Saved (v5) only once a refit has opened.
   */
  refitOpenDays: Int32Array
}

/** A standing change for the Career to apply (S-M2-13). */
export interface StandingDelta {
  factionId: FactionId
  delta: number
  reason: string
  /**
   * A loss never takes the standing below this (D-335 D4: a guardian kill stops at outlaws.json
   * `guardian.holdfastKillFloor`, −49); a standing already below it is left alone. Absent: no floor.
   */
  floor?: number
}

/**
 * A guardian ship the player destroyed (M7 spec §12.1 "logging now"): the faction day, the system id and the ship's
 * agent id. Queued by `outlaws/guardian.ts` `recordPlayerKill` for `AdvanceResult.guardianKills`; the Career logs it.
 * M7 (S1) adds the hull id, the pilot's name seed and `selfDefence` (present only when true).
 */
export interface GuardianKill {
  day: number
  systemId: string
  shipId: string
  hull?: string
  pilotSeed?: number
  selfDefence?: boolean
}

/** The player mirror (M5 spec §12, S-M2-13). Per-faction arrays are indexed by faction. */
export interface PlayerCharterStore {
  /** The Career's standings, mirrored (`playerStandings`); `guardian` is hidden and worker-only. */
  standing: Float32Array
  bounty: Float64Array
  /** Faction day of the last crime in that jurisdiction (−1 = never). */
  lastCrimeDay: Int32Array
  /** The assault throttle (once per victim per 60 s): a small ring of victim keys and times. */
  assaultKey: Int32Array
  assaultT: Float64Array
  assaultHead: number
  /** Deltas queued for `AdvanceResult.standingDeltas` (never applied to the mirror; the Career is the source of truth). */
  pendingDeltas: StandingDelta[]
  /** Guardian kills queued for `AdvanceResult.guardianKills` (drained every advance). */
  pendingGuardianKills: GuardianKill[]
  /** Wrecks the player murdered: wreck id → strategic time (piracy, 10 min). */
  murderedWrecks: Map<string, number>
  /** The player attacked guardians (their grudge, M5 spec §9.2). */
  guardianGrudge: boolean
}

/** Combat zones (M5 spec §8.3), a slot pool. */
export interface ZoneStore {
  capacity: number
  count: number
  nextSeq: number
  active: Uint8Array
  seq: Uint32Array
  sys: Uint16Array
  /** The field or POI node. */
  node: Int32Array
  sideA: Uint8Array
  sideB: Uint8Array
  opened: Float64Array
  until: Float64Array
  strengthA: Float32Array
  strengthB: Float32Array
  /** The player's zoneSupport damage per side. */
  supportA: Float32Array
  supportB: Float32Array
  /** The off-screen encounter resolving it (−1 = none). */
  encounter: Int32Array
}

/** Raider clans (M5 spec §9.1), one per raider base. */
export interface ClanStore {
  capacity: number
  count: number
  active: Uint8Array
  /** The base node (−1 = none) and its system. */
  baseNode: Int32Array
  sys: Uint16Array
  /** The clan's share of the raider treasury. */
  treasury: Float64Array
  aggression: Float32Array
  heat: Float32Array
  bands: Uint8Array
  /** Quiet (sorties ×0.3) until this faction day, and the base disabled until this day (−1 = no). */
  quietUntilDay: Int32Array
  baseDisabledUntilDay: Int32Array
  /** Index into outlaws.json clan names; the clan nearest home is Red Tally. */
  nameIndex: Uint16Array
}

/** The guardian cell (M5 spec §9.2; hidden). */
export interface GuardianCellState {
  /** The guardian base node (−1 = none yet) and its system (−1). */
  baseNode: number
  sys: number
  /** Aggression A, 0..100. */
  aggression: number
  /** Strategic time of the next sortie. */
  nextSortieT: number
  /** Tetherstone extracted per day: a 7-day ring and its head. */
  extraction: Float32Array
  extractionHead: number
}

/** A Charter vote (M5 spec §11). */
export interface CharterVote {
  id: string
  measure: MeasureName
  proposedDay: number
  /** proposedDay + votes.json delayDays. */
  voteDay: number
  sponsor: FactionId | 'player'
  /** 0..1. */
  evidenceWeight: number
  ballots: { faction: FactionId; weight: number; yes: boolean }[]
  result: 'pending' | 'passed' | 'failed'
}

export interface VoteStore {
  /** votes.json capacity: votes kept (oldest dropped). */
  capacity: number
  list: CharterVote[]
  /** Faction day of the last proposal (−1 = never; the cooldown). */
  lastProposedDay: number
  nextSeq: number
}

/** Gate stages and the Isolated Era (M5 spec §10). */
export interface GateStageState {
  /** GATE_STAGE code. */
  stage: number
  /** Strategic time a tremor closure ends (−1 = open). */
  closedUntil: number
  /** Consecutive days above the reopen threshold while closed. */
  reopenStreak: number
  /** The global Isolation state's STAGE and the days left in it. */
  isolation: number
  isolationDaysLeft: number
  /** The local flicker VFX rate 0..1 (`(60 − s) / 60` in flickers). */
  flickerRate: number
}

/**
 * M8 (plan §4.5 SNK-6b1): the pilot hands `qty` t of `ware` in to works site `siteId` (`site-<seq>`) for the open
 * works request on it. `expectedReward` is the credits the client quoted (the view row's premium × `qty` × `unit`);
 * the worker prices the hand-in itself and refuses `priceMoved` beyond the 2% player tolerance.
 */
export interface WorksDeliverCommand {
  type: 'worksDeliver'
  siteId: string
  ware: WareId
  qty: number
  expectedReward: number
}

/** `worksDeliver` refusals (each changes nothing). */
export type WorksDeliverError = 'treasuryShort' | 'siteGone' | 'notSupplying' | 'notNeeded' | 'priceMoved'

/**
 * M9 (plan CH-1b, D-TBD M9-5): the player opens (`open` true: disabled → open claim) or abandons (false: open →
 * disabled, with a fresh `fleet.json claim.lifetimeDays`) a claim on off-screen wreck `wreckId` (`ow-<seq>`). With
 * CH-7a's registration it is the only worker write to a hulk: nothing in the worker claims one on its own.
 */
export interface MarkClaimCommand {
  type: 'markClaim'
  wreckId: string
  open: boolean
}

/** `markClaim` refusals (each changes nothing): no live, unexpired record; not disabled; not an open claim; `maxOpen` reached. */
export type MarkClaimError = 'unknownWreck' | 'notDisabled' | 'notOpenClaim' | 'tooManyOpenClaims'

/**
 * M9 (plan CH-7a, D-TBD M9-8): the claimed-ship commands (`fleet/claims.ts`), the player's only worker writes to a
 * hulk besides `markClaim`. Ship ids are `ps-<seq>` (the plan's `fleetId`, the Career's `OwnedShip.fleetId`).
 *
 * - `registerClaimedShip`: a completed kit becomes a fleet ship of `hull` in space at the hulk (the node of `systemId`
 *   nearest `pos`), on a ferry order to station `dockAt`, unregistered (uninsurable) until it docks. `hulkKey` is a
 *   seeded hulk's `hk:<sys>:<n>` or an off-screen wreck's `ow-<seq>` (that record must be an open claim; it is
 *   removed). `flaws` is the hulk's Uint16 bitmask (bits 0–11 flaws, 12–15 the FR edge) and `valueMult` the Career's
 *   insured factor (CH-8b: `hulks/insure.ts claimedValueMult`, what the kit put in, at most `valueMultOf(flaws)`).
 *   The result's `createdId` is the new ship.
 * - `setHullFlaws`: a yard at `stationId` removes flaws: the new bitmask must be a subset of the ship's.
 * - `scrapClaimedShip`: a registered claimed ship docked at a yard is scrapped (its slot freed like a sale; no
 *   insurance claim). The scrap value is the Career's (CH-11), so the result carries no `walletDelta`.
 */
export interface RegisterClaimedShipCommand {
  type: 'registerClaimedShip'
  hulkKey: string
  hull: string
  flaws: number
  valueMult: number
  systemId: string
  pos: [number, number, number]
  dockAt: string
}
export interface SetHullFlawsCommand {
  type: 'setHullFlaws'
  shipId: string
  flaws: number
  valueMult: number
  stationId: string
}
export interface ScrapClaimedShipCommand {
  type: 'scrapClaimedShip'
  shipId: string
}
export type ClaimedShipCommand = RegisterClaimedShipCommand | SetHullFlawsCommand | ScrapClaimedShipCommand

/**
 * Claimed-ship refusals (each changes nothing): `hulkUsed` (the key was registered before), `notClaimed` (not a
 * claimed ship), `notYard` (the station has no yard), `notRegistered` (a claimed ship still on its first ferry: no
 * insurance yet, H-6).
 */
export type ClaimedShipError = 'hulkUsed' | 'notClaimed' | 'notYard' | 'notRegistered'

/** What a `worksDeliver` did: `accepted` t went into the site at `paidT` credits per t (premium × unit), `paid` in all. */
export interface WorksDeliverOutcome {
  accepted: number
  paidT: number
  paid: number
}

/** A faction request for M2's contract board (M5 spec §7.3, S-M2-12). */
export interface FactionRequest {
  id: string
  faction: FactionId
  systemId: string
  stationId: string | null
  kind: RequestKind
  /** deliver: a ware short at one of its stations. */
  ware?: WareId
  qty?: number
  /** zoneSupport. */
  zoneId?: string
  /**
   * M8 works request (plan SNK-6b1; kind `deliver`): the works site (`site-<seq>`) it supplies. Absent on every other
   * request and on every request in a v4 save. `ware` is the site's largest missing ware and `qty` its missing tonnes
   * at publish.
   */
  siteId?: string
  /** M8 works request: the credits per t fixed at publish (the ware's base price), before the premium. */
  unit?: number
  /** M8 works request: the day it was published; the premium grows with the days since. */
  openedDay?: number
  reward: number
  expiresDay: number
}

/**
 * A request row on the system view (M5 spec §7.3). A works request (M8 SNK-6b1) carries `premium`, today's
 * multiplier on `unit`; its `qty` is the tonnes still missing now and `reward` = premium × qty × unit (rounded).
 */
export interface FactionRequestView extends FactionRequest {
  premium?: number
}

export interface RequestStore {
  /** ai.json requests.capacity. */
  capacity: number
  list: FactionRequest[]
  nextSeq: number
}

// ---------------------------------------------------------------------------------------------
// M5 views (M5 spec §16). C11 adds `claim` and `zones` to SystemEconomyView and the summaries to GalaxyEconomyView.
// ---------------------------------------------------------------------------------------------

/** A corporation claim's display security band (M5 spec §27.5, D-424 D3): corp.json `claims.securityBands`. */
export type SecurityBand = 'low' | 'medium' | 'high'

/** The corporation part of a claim view (M5 spec §27.7): set only when the player corporation holds the system. */
export interface ClaimCorpView {
  /** The corporation's name. */
  name: string
  /** Its claim Security band in the system (display only). */
  band: SecurityBand
  /** A manual tariff is set (`corp.tariff[sys] >= 0`); an unset (automatic) tariff charges 0 % (§27.6). */
  tariffSet: boolean
}

/**
 * The Charter claim of a system (M5 spec §16). Since D-424 (§27.7) the player corporation shows as `'player'`
 * (`CorpFactionId`) in `holder`, `flip.challenger` and `scores[].faction`; `'player'` is never a law faction and
 * never a `FACTION` key.
 */
export interface ClaimView {
  systemId: string
  holder: CorpFactionId | null
  tariff: number
  toll: number
  contested: boolean
  flip: { challenger: CorpFactionId; daysLeft: number } | null
  scores: { faction: CorpFactionId; ops: number; sec: number; inf: number; s: number; trend: number[] }[]
  states: { kind: StateKind; stage: 'pending' | 'active' | 'recovering'; daysLeft: number }[]
  /** The corporation's claim (name, security band, tariff set), or null when the corporation doesn't hold the system. */
  corp: ClaimCorpView | null
}

export interface RelationsView {
  /** Ladder pairs; after founding (M6) the player corporation appears as `'player'`. */
  pairs: { a: CorpFactionId; b: CorpFactionId; relation: RelationName; pressure: number }[]
  player: {
    standings: Partial<Record<FactionId, number>>
    bounties: Partial<Record<FactionId, number>>
    wanted: Partial<Record<FactionId, WantedLevel>>
  }
}

export interface GateView {
  stage: GateStageName
  closedUntil: number | null
  isolation: boolean
  /** Only from story tier 3. */
  stability: number | null
  /** M7 (plan S19): extra Tethys fracture sites shown for the story ramp (`state.story.rigs`); absent when 0. */
  storyRigs?: number
}

// ---------------------------------------------------------------------------------------------
// M5 commands (M5 spec §16). C11 joins `CharterCommand` into `StrategicCommand` when it wires `commands.ts`.
// ---------------------------------------------------------------------------------------------

export type CharterDevCommand =
  | { type: 'dev'; op: 'forceRelation'; a: FactionId; b: FactionId; relation: RelationName }
  | { type: 'dev'; op: 'forceState'; systemId: string; kind: StateKind; stage: StageName }
  | { type: 'dev'; op: 'forceClaim'; systemId: string; holder: CorpFactionId | null }
  | { type: 'dev'; op: 'runVoteNow' }

/** A Wire item posted by M7 (`postWire`). */
export interface PostedWireItem {
  headline: string
  severity: number
  systemId?: string
  /** M7 (S26): the outlet that ran a story item (`WireItem.source`); absent = the neutral Wire. */
  source?: StoryWireOutlet
}

export type CharterCommand =
  | { type: 'reportCrime'; kind: CrimeKind; victimFaction: FactionId; systemId: string; value: number }
  | { type: 'payFines' }
  | { type: 'zoneSupport'; zoneId: string; side: 'a' | 'b'; damage: number }
  | { type: 'tollPaid'; relayId: string; amount: number }
  | { type: 'tollEvaded'; relayId: string }
  | { type: 'playerContractDone'; factionId: FactionId; systemId: string; reward: number }
  | { type: 'relinkSponsored'; relayId: string; factionId: FactionId }
  | { type: 'playerStandings'; standings: Partial<Record<FactionId, number>> }
  | { type: 'playerStandingDelta'; factionId: FactionId; delta: number; reason: string }
  | { type: 'setStoryTier'; tier: number }
  | { type: 'setWorldFlag'; key: string; value: number | string | boolean }
  | { type: 'postWire'; item: PostedWireItem }
  | { type: 'proposeCharterVote'; measure: MeasureName; sponsor: FactionId | 'player'; evidenceWeight: number }
  | CharterDevCommand
export type CharterCommandType = CharterCommand['type']

export type CharterCommandError =
  | 'unknownSystem'
  | 'unknownRelay'
  | 'unknownZone'
  | 'unknownFaction'
  | 'inZone'
  | 'outlaw'
  | 'cooldown'
  | 'devDisabled'
  | 'invalid'

export type CharterCommandResult =
  | {
      ok: true
      /** payFines: the amount the Career pays. */
      amount?: number
      /** Standing deltas the command produced (the Career applies them). */
      standingDeltas?: StandingDelta[]
    }
  | { ok: false; error: CharterCommandError }

// ---------------------------------------------------------------------------------------------
// M5 save v2 (M5 spec §17). C11 makes it the current version (`StrategicSave`) with the real migration.
// ---------------------------------------------------------------------------------------------

export interface CharterSaveZone {
  id: string
  systemId: string
  node: number
  sideA: FactionId
  sideB: FactionId
  opened: number
  until: number
  strengthA: number
  strengthB: number
  supportA: number
  supportB: number
  /**
   * The zone's store slot (`state.zones`), written only when it differs from the record's index in the list (a free
   * slot sat before it at save time; absent = that index, so saves without holes and older saves are unchanged). The
   * load puts the zone back in it, so the next `openZone` takes the same free slot and zones step, close and are
   * picked in the same order as in the original run (D-1050: charter zone slots kept through save/load).
   */
  slot?: number
}

export interface CharterSaveClan {
  /** The clan's base node; a disbanded clan keeps its last one (raiders still docked there wait out the disable). */
  baseNode: number
  /** False once the clan disbanded (absent in older saves, which wrote baseNode −1 for a disbanded clan). */
  active?: boolean
  systemId: string
  treasury: number
  aggression: number
  heat: number
  bands: number
  quietUntilDay: number
  baseDisabledUntilDay: number
  nameIndex: number
}

/** Everything M5 adds to the save (M5 spec §17). Typed-array stores are base64 columns (the v1 codec). */
export interface CharterSave {
  /** The player standings mirror, by faction id (guardian hidden). */
  standings: Partial<Record<FactionId, number>>
  /** Columns: pressure, relation, weariness, relationSince, warEndDay (R2-T4; absent in older saves = −1), standing, warLossValue. */
  relations: Record<string, string>
  /** Columns: cooldownUntil, startTreasury, relinkRelay, relinkUntil, unreplacedLosses, targetCap (T7b; absent in older saves = 0). */
  factionAi: Record<string, string>
  /** Columns: the claim EMAs, today's accumulators, score, trend, holder, streaks, tariff, toll. */
  claims: { trendHead: number; trendFilled: number; cols: Record<string, string> }
  /** Columns: stage, daysLeft, triggerDays, grievance and the effect columns. */
  states: Record<string, string>
  /** Columns: disabledBy, the disable streaks, harassment. */
  stations: Record<string, string>
  zones: CharterSaveZone[]
  law: {
    bounties: Partial<Record<FactionId, number>>
    lastCrimeDay: Partial<Record<FactionId, number>>
    murderedWrecks: [string, number][]
    guardianGrudge: boolean
  }
  votes: { list: CharterVote[]; lastProposedDay: number; nextSeq: number }
  /** Ware ids that are illegal. */
  illegal: WareId[]
  gateStage: { stage: GateStageName; closedUntil: number; reopenStreak: number; isolation: StageName; isolationDaysLeft: number }
  storyTier: number
  flags: [string, number | string | boolean][]
  clans: CharterSaveClan[]
  guardian: { baseNode: number; aggression: number; nextSortieT: number; extraction: number[] }
  requests: { list: FactionRequest[]; nextSeq: number }
  zoneSeq: number
}

export interface StrategicSaveV2 extends Omit<StrategicSaveV1, 'version'> {
  version: 2
  charter: CharterSave
}

// =============================================================================================
// M6: Pilot to CEO (M6 spec; plan P0). Append-only; frozen at the M6 fork commit so the M6 core waves build in
// parallel. P1 allocates every store in `allocateState`; the M6 day slices and commands stay unwired until P15.
// =============================================================================================

/**
 * The faction index the player corporation takes (plan §2, D-TBD): P1's `addFaction` claims it inside the
 * preallocated `caps.factionCapacity` on the first purchase. It is the first index after factions.json.
 */
export const PLAYER_SLOT = 8
/** The player corporation's faction id at the boundary (views, saves), as M5's vote sponsor `'player'`. */
export const PLAYER_FACTION_ID = 'player'
/** A faction id at the boundary once the player slot exists. */
export type CorpFactionId = FactionId | typeof PLAYER_FACTION_ID

/**
 * Ship classes (M6 spec §4; M10 spec §3.1). Indices 0-7 are the M6 classes, named after the player-buyable hull ids
 * (hulls.json `class`, P5); 8-13 are the M10 role classes appended by P2b1, which no hull uses until P4. Several
 * hulls may share a class. Append only: the FLEET_COLS `shipClass` column saves the index.
 */
export const SHIP_CLASS = {
  light_fighter: 0,
  interceptor: 1,
  gunship: 2,
  miner: 3,
  salvager: 4,
  hauler: 5,
  hauler_large: 6,
  corvette: 7,
  bomber: 8,
  frigate: 9,
  courier: 10,
  tug: 11,
  patrol: 12,
  survey: 13,
} as const
export type ShipClassName = keyof typeof SHIP_CLASS
export const SHIP_CLASS_NAMES = [
  'light_fighter',
  'interceptor',
  'gunship',
  'miner',
  'salvager',
  'hauler',
  'hauler_large',
  'corvette',
  'bomber',
  'frigate',
  'courier',
  'tug',
  'patrol',
  'survey',
] as const satisfies readonly ShipClassName[]
export const SHIP_CLASS_COUNT = 14

/** Standing orders (M6 spec §6.1), in the spec's table order. */
export const ORDER = { mine: 0, trade: 1, supply: 2, salvage: 3, patrol: 4, escort: 5, ferry: 6, hold: 7 } as const
export type OrderName = keyof typeof ORDER
export const ORDER_NAMES = ['mine', 'trade', 'supply', 'salvage', 'patrol', 'escort', 'ferry', 'hold'] as const satisfies readonly OrderName[]
export const ORDER_COUNT = 8

/** Rules of engagement (M6 spec §6.1): evade = flee in the first round, defend = fight back, aggressive = engage. */
export const ROE = { evade: 0, defend: 1, aggressive: 2 } as const
export type RoeName = keyof typeof ROE
export const ROE_NAMES = ['evade', 'defend', 'aggressive'] as const satisfies readonly RoeName[]

/** Stop conditions (M6 spec §6.1), bits of `FleetStore.stops`: the order drops to `hold` when one is met. */
export const STOP = { holdFull: 1, budgetSpent: 2, date: 4 } as const
export type StopName = keyof typeof STOP
export const STOP_NAMES = ['holdFull', 'budgetSpent', 'date'] as const satisfies readonly StopName[]

/** Mine and salvage sell policy (M6 spec §6.1). */
export const SELL_POLICY = { best: 0, myStation: 1, hold: 2 } as const
export type SellPolicyName = keyof typeof SELL_POLICY
export const SELL_POLICY_NAMES = ['best', 'myStation', 'hold'] as const satisfies readonly SellPolicyName[]

/** Patrol rules (M6 spec §6.1). */
export const PATROL_RULE = { defendAssets: 0, engageHostiles: 1, guardLane: 2 } as const
export type PatrolRuleName = keyof typeof PATROL_RULE
export const PATROL_RULE_NAMES = ['defendAssets', 'engageHostiles', 'guardLane'] as const satisfies readonly PatrolRuleName[]

/** How an order's area is given (M6 spec §6.1): N jumps from home, or the explicit system set `FleetStore.area`. */
export const AREA_MODE = { fromHome: 0, systems: 1 } as const
export type AreaModeName = keyof typeof AREA_MODE
export const AREA_MODE_NAMES = ['fromHome', 'systems'] as const satisfies readonly AreaModeName[]

/** `ORDER_PARAM.escort` value meaning "escort the player's own ship" (P8 parks the agent in the player's system). */
export const ESCORT_PLAYER = -2

/**
 * The per-ship behaviour parameter row (plan §2; P3 compiles it, P7/P8 read it through `paramRow`): a Float32 block
 * of `ORDER_PARAM_COUNT` per fleet slot. Entries 0-9 are the M4 role limits (the default is the order's role row
 * in roles.json, so a default order behaves exactly like an M4 agent); the rest are the spec §6.1 order
 * parameters. Station, agent, relay and ware references are indices (−1 = none); ware sets are bitmasks over ware
 * indices (bit w; `WARE_COUNT` ≤ 24 keeps them exact in float32).
 */
export const ORDER_PARAM = {
  /** trader / supplier `jumpRange`; the salvage search range. */
  jumpRange: 0,
  /** trader `minMargin`. */
  minMargin: 1,
  /** trader / miner `riskWeight`. */
  riskWeight: 2,
  /** trader `maxRisk`: the order's risk limit, a route filter for player orders (a route whose `riskWeight × danger` is above it is never taken; M6I-1). AI agents: the penalty cap. */
  maxRisk: 3,
  /** trader `minLot`. */
  minLot: 4,
  /** miner `homeRange`: the "N jumps from home" area. */
  homeRange: 5,
  /** miner `sellRange`: "best price within N jumps". */
  sellRange: 6,
  /** supplier `restockMargin`. */
  restockMargin: 7,
  /** supplier `minDeficit`. */
  minDeficit: 8,
  /** patrol `respondRange`. */
  respondRange: 9,
  /** AREA_MODE. */
  areaMode: 10,
  /** Allowed wares (trade) or ores (mine), a bitmask; `ALL_WARES_MASK` = any. */
  wareAllow: 11,
  /** Blocked wares, a bitmask (tetherstone can be excluded). */
  wareBlock: 12,
  /** SELL_POLICY. */
  sellPolicy: 13,
  /** The player station a `myStation` policy delivers to (station index, −1). */
  sellStation: 14,
  /** Trade capital allowance, credits drawn from the company account. */
  capital: 15,
  /** Supply buying budget, credits (the `budgetSpent` stop). */
  budget: 16,
  /** Supply target or ferry destination (station index, −1). */
  target: 17,
  /** Supply input priority, wares in order (ware index, −1 = none). */
  priority0: 18,
  priority1: 19,
  priority2: 20,
  priority3: 21,
  /** PATROL_RULE. */
  patrolRule: 22,
  /** `guardLane`: the relay index (−1). */
  laneRelay: 23,
  /** Escort target: agent index, ESCORT_PLAYER, −1 = none. */
  escort: 24,
  /** `STOP.date`: the faction day the order stops (−1 = none). */
  stopDay: 25,
} as const
export type OrderParamName = keyof typeof ORDER_PARAM
export const ORDER_PARAM_NAMES = Object.keys(ORDER_PARAM) as readonly OrderParamName[]
export const ORDER_PARAM_COUNT = 26
/** The role-row params (0 … ROLE_PARAM_COUNT − 1): their defaults come from roles.json. */
export const ROLE_PARAM_COUNT = 10
/** Supply priority slots (`priority0` … `priority3`). */
export const ORDER_PRIORITY_SLOTS = 4
/** `wareAllow` value meaning every ware (bits 0 … WARE_COUNT − 1; derived, never a literal: MC-e1). */
export const ALL_WARES_MASK = (1 << WARE_COUNT) - 1

/** Captain skills 1..5 (M6 spec §5), the column order of `CaptainStore.skills`. */
export const SKILL = { piloting: 0, trade: 1, mining: 2, salvage: 3, combat: 4, leadership: 5 } as const
export type SkillName = keyof typeof SKILL
export const SKILL_NAMES = ['piloting', 'trade', 'mining', 'salvage', 'combat', 'leadership'] as const satisfies readonly SkillName[]
export const SKILL_COUNT = 6

/**
 * Captain traits (M6 spec §5), bits `1 << code` of `CaptainStore.traits`. Named `CAPTAIN_TRAIT` because M5's
 * `TRAIT` is the faction personality table (plan P0 deviation).
 */
export const CAPTAIN_TRAIT = {
  cautious: 0,
  reckless: 1,
  greedy: 2,
  veteran: 3,
  exDescendant: 4,
  exMegacorp: 5,
  unionist: 6,
  loyal: 7,
} as const
export type CaptainTraitName = keyof typeof CAPTAIN_TRAIT
export const CAPTAIN_TRAIT_NAMES = [
  'cautious',
  'reckless',
  'greedy',
  'veteran',
  'exDescendant',
  'exMegacorp',
  'unionist',
  'loyal',
] as const satisfies readonly CaptainTraitName[]
export const CAPTAIN_TRAIT_COUNT = 8

/** Captain states (M6 spec §5). A striking captain's ship idles (per ship; M5's system Strike state is untouched). */
export const CAPTAIN_STATE = { candidate: 0, hired: 1, recovering: 2, striking: 3, quit: 4, deserted: 5, dead: 6 } as const
export type CaptainStateName = keyof typeof CAPTAIN_STATE
export const CAPTAIN_STATE_NAMES = [
  'candidate',
  'hired',
  'recovering',
  'striking',
  'quit',
  'deserted',
  'dead',
] as const satisfies readonly CaptainStateName[]

/** Player station types (M6 spec §7.2). The names are the M4 template ids they build (P6 adds `defense_platform`). */
export const STATION_TYPE = { outpost: 0, refinery: 1, ice_works: 2, fabworks: 3, exchange: 4, hab: 5, defense_platform: 6 } as const
export type StationTypeName = keyof typeof STATION_TYPE
export const STATION_TYPE_NAMES = [
  'outpost',
  'refinery',
  'ice_works',
  'fabworks',
  'exchange',
  'hab',
  'defense_platform',
] as const satisfies readonly StationTypeName[]
export const STATION_TYPE_COUNT = 7

/** Corporate milestones (M6 spec §7.3), bits `1 << code` of `CorpRecord.milestones`. No win screen. */
export const MILESTONE = {
  firstExtraShip: 0,
  corpFounded: 1,
  firstStation: 2,
  firstClaim: 3,
  fiveClaims: 4,
  regionDominance: 5,
  decisiveVote: 6,
} as const
export type MilestoneName = keyof typeof MILESTONE
export const MILESTONE_NAMES = [
  'firstExtraShip',
  'corpFounded',
  'firstStation',
  'firstClaim',
  'fiveClaims',
  'regionDominance',
  'decisiveVote',
] as const satisfies readonly MilestoneName[]
export const MILESTONE_COUNT = 7

/** Docking rights of a system the player holds (M6 spec §7.3). */
export const DOCKING_RIGHTS = { open: 0, denyHostile: 1, denyFaction: 2 } as const
export type DockingRightsName = keyof typeof DOCKING_RIGHTS
export const DOCKING_RIGHTS_NAMES = ['open', 'denyHostile', 'denyFaction'] as const satisfies readonly DockingRightsName[]

/** Player station price policy (M6 spec §7.2): auto = the M4 curve, manual = clamped to min/max per ware. */
export const PRICE_POLICY = { auto: 0, manual: 1 } as const
export type PricePolicyName = keyof typeof PRICE_POLICY
export const PRICE_POLICY_NAMES = ['auto', 'manual'] as const satisfies readonly PricePolicyName[]

/** Player station trade permissions (M6 spec §7.2). */
export const TRADE_PERMISSION = { all: 0, corp: 1, nonHostile: 2 } as const
export type TradePermissionName = keyof typeof TRADE_PERMISSION
export const TRADE_PERMISSION_NAMES = ['all', 'corp', 'nonHostile'] as const satisfies readonly TradePermissionName[]

/** A player ship's status (views, the fleet feed). P7/P8 keep it current. */
export const SHIP_STATUS = { docked: 0, working: 1, travelling: 2, ferrying: 3, transfer: 4, striking: 5, idle: 6, lost: 7 } as const
export type ShipStatusName = keyof typeof SHIP_STATUS
export const SHIP_STATUS_NAMES = [
  'docked',
  'working',
  'travelling',
  'ferrying',
  'transfer',
  'striking',
  'idle',
  'lost',
] as const satisfies readonly ShipStatusName[]

/**
 * M6 treasury categories (plan P2), appended after M5's. `purchases`, `construction` and M5's `charterFees` are
 * reused. `LEDGER_COUNT` stays 15 at P0: raising it to `LEDGER_COUNT_M6` widens the v2 ledgers, so it goes with
 * the v3 save and its migration (see the P0 report).
 */
export const LEDGER_M6 = { fleetIncome: 15, wages: 16, insurance: 17, stationIncome: 18, transfersIn: 19, transfersOut: 20 } as const
export type LedgerM6Category = keyof typeof LEDGER_M6
export const LEDGER_COUNT_M6 = 21

/**
 * M10 treasury categories (M10 P3a1, save v8; D-771), appended after M6's. `royalty` is an inter-faction transfer
 * (maker ← non-maker yard owner), booked from P4a1c; nothing books it in P3. It is **not** in `votes.ts INCOME` (the M7
 * ballot) and **not** in `treasury.ts EXTERNAL_LEDGERS`.
 */
export const LEDGER_M10 = { royalty: 21 } as const
export type LedgerM10Category = keyof typeof LEDGER_M10
export const LEDGER_COUNT_M10 = 22

/*
 * M6 event fields (unused fields as for M4: station/agent/aux −1, ware −1, faction/other FACTION_NONE, numbers 0).
 * `faction` is the player slot (PLAYER_SLOT) unless noted; `agent` is the ship's agent index (−1 in the transfer
 * service); captains are `CaptainStore` indices in `aux` unless noted.
 * - `shipBought`: sys, station = yard, other = the yard owner, value = price, cause = hull index, aux = order seq.
 * - `orderSet`: sys, agent, cause = ORDER, qty = preset index + 1 (0 = custom), aux = fleet slot.
 * - `orderStopped`: sys, agent, cause = the STOP bit met, aux = fleet slot.
 * - `captainHired` / `captainFired`: station = where hired (−1), agent = the ship (−1), aux, value = wage.
 * - `captainQuit`: agent (−1), aux, value = loyalty.
 * - `captainDeserted`: sys, agent (now the raider's), other = the new faction, aux, cause = hull index.
 * - `captainDied`: sys, agent, aux, other = killer faction; `captainRecovered`: aux, sys = where it recovered.
 * - `skillUp`: aux, cause = SKILL, qty = the new level.
 * - `strike`: agent, aux, qty = days; `wagesLate`: aux, value = the amount owed, qty = days late.
 * - `corpFounded`: value = the fee. `charterBought`: sys, value = price.
 * - `stationCommissioned`: sys, aux = site seq, cause = STATION_TYPE, qty = level, value = the estimated cost.
 * - `commissionBuilt` / `stationUpgraded`: sys, station, cause = STATION_TYPE, qty = the level.
 * - `milestone`: cause = MILESTONE, sys (the claim's system for claim milestones, else SYS_GLOBAL).
 * - `assetAttacked`: sys, agent or station (the other −1), other = attacker faction (FACTION_NONE unknown).
 * - `fleetEarning`: sys, agent, ware, qty t, value = the run's net credits, cause = ORDER, aux = fleet slot.
 * - `shipTransferred`: sys (destination), station, agent (the new agent), value = fee, qty = days, aux = fleet slot.
 * - `insurancePaid`: sys, agent (the lost one), value = payout, cause = hull index, aux = fleet slot.
 */

// ---------------------------------------------------------------------------------------------
// M6 stores (plan §2). P1 allocates them; nothing writes them until the M6 tasks do.
// ---------------------------------------------------------------------------------------------

/**
 * Player ships (M6 spec §4, §6.1), a slot pool of `caps.playerShips` with a free-index stack. The slot is the
 * ship's identity (`ps-<seq>`): the transfer service re-creates the agent, so `agent` changes and the slot stays.
 * The hot loops look the slot up per agent through `slotOf` (−1 = not a player ship).
 */
export interface FleetStore {
  capacity: number
  count: number
  high: number
  free: Int32Array
  freeTop: number
  alive: Uint8Array
  /** Per AGENT index (pool capacity): the fleet slot, −1 = not a player ship. */
  slotOf: Int16Array
  /** Per slot: the agent index, −1 while in the transfer service or lost. */
  agent: Int32Array
  /** Ship id = `ps-<seq>`. */
  seq: Uint32Array
  nextSeq: number
  /** Ship names (seeded default; the player may rename). */
  name: string[]
  hull: Uint8Array
  /** SHIP_CLASS. */
  shipClass: Uint8Array
  /** ORDER, ROE and STOP bits of the standing order. */
  order: Uint8Array
  roe: Uint8Array
  stops: Uint8Array
  /** Preset index + 1 (orders.json presets order), 0 = custom. */
  preset: Uint8Array
  /** `slot × ORDER_PARAM_COUNT + ORDER_PARAM.x`: the compiled row (P3 `compileOrder`). */
  params: Float32Array
  /** 32-bit words per slot in `area` (ceil(systems / 32)). */
  areaWords: number
  /** `slot × areaWords + (sys >> 5)`, bit `sys & 31`: the order's systems (AREA_MODE.systems, patrol systems). */
  area: Uint32Array
  /** Player-defined fleet group (filters, bulk presets), 0 = none. */
  group: Uint8Array
  /** Captain index (CaptainStore), −1 = none (neutral modifiers). */
  captain: Int16Array
  /** 1 = insured (M6 spec §4). */
  insured: Uint8Array
  /** 1 = an escape pod is fitted (captain survival; the Career hangar sends it). */
  escapePod: Uint8Array
  /** SHIP_STATUS. */
  status: Uint8Array
  /** Transfer service: the destination station (−1 = none) and the arrival time, strategic s. */
  transferTo: Int32Array
  transferUntil: Float64Array
  /** Credits drawn under the current order (capital, supply budget; the `budgetSpent` stop). */
  spent: Float64Array
  /** Net earnings today, yesterday and in total (views: earnings per day). */
  earnedToday: Float64Array
  earnedYesterday: Float64Array
  earnedTotal: Float64Array
  /** Faction day the ship joined the fleet. */
  boughtDay: Int32Array
  /** M9 (CH-1a; save v6): the claimed hull's flaw bitmask (hulks.json flaw bits), 0 = none (every bought ship). */
  flaws: Uint16Array
  /** M9 (CH-1a; save v6): the insured-value factor of a claimed hull (CH-8), 1 = pristine (every bought ship). */
  valueMult: Float32Array
}

/**
 * Captains (M6 spec §5): one pool. Slots `[0, hiredCapacity)` hold hired captains (and the recovering, striking
 * and quit ones until released); station `s`'s roster candidates live at `hiredCapacity + s × rosterMax + k`,
 * rolled from the seed each faction day. Hiring copies a candidate into a free hired slot.
 */
export interface CaptainStore {
  capacity: number
  /** captains.json `capacity.hired` (64). */
  hiredCapacity: number
  /** Hired slots in use. */
  hiredCount: number
  /** captains.json `roster.size[1]`. */
  rosterMax: number
  /** Per station: candidates in its roster (0..rosterMax) and the faction day it was rolled (−1 = never). */
  rosterCount: Uint8Array
  rosterDay: Int32Array
  alive: Uint8Array
  /** CAPTAIN_STATE. */
  state: Uint8Array
  /** `c × SKILL_COUNT + SKILL.x`: 1..5. */
  skills: Uint8Array
  /** `c × SKILL_COUNT + SKILL.x`: XP toward the next level of each skill. */
  xp: Float32Array
  /** CAPTAIN_TRAIT bits. */
  traits: Uint8Array
  /** Wage per faction day, credits. */
  wage: Float32Array
  /** 0..100. */
  loyalty: Float32Array
  /** Unpaid wages owed, credits, and the days they are late. */
  owed: Float32Array
  lateDays: Uint8Array
  /** Fleet slot commanded (−1), or the commission it manages (−1). One job at a time. */
  ship: Int16Array
  managerOf: Int16Array
  /** Faction day a survivor is fit again, or a strike ends (−1 = none). */
  recoverUntilDay: Int32Array
  strikeUntilDay: Int32Array
  /** The faction whose station rostered the captain (flavour; faction index). */
  origin: Uint8Array
  /** Placeholder portrait 0..captains.json `portraits − 1` (code-drawn silhouettes, D-TBD). */
  portrait: Uint8Array
  /** Seed of the name (captains.json syllables). */
  nameSeed: Uint32Array
  hiredDay: Int32Array
  /** Completed runs and kills (memorial, logs). */
  runs: Uint16Array
  kills: Uint16Array
  /** Captain id = `cap-<seq>`. */
  seq: Uint32Array
  nextSeq: number
}

/** The player's livery colours (`#rrggbb`), fed into the faction hull-material uniforms (P23). */
export interface LiveryColors {
  primary: string
  secondary: string
  accent: string
}

/** A code-drawn logo (M6 spec §7.1, D-TBD placeholders): a seeded emblem kit, the draft picked and the finishing. */
export interface LogoRecipe {
  seed: number
  /** Draft picked, 0..corp.json `logo.drafts − 1`. */
  draft: number
  shape: number
  palette: number
  /** The player's finishing steps (recorded as `humanContribution`, D-026). */
  finishing: string[]
}

/** The player corporation (M6 spec §7.1). One per game; a sole trader until founding. */
export interface CorpRecord {
  /** The player slot's faction index, −1 until the first purchase (P1 `addFaction`). */
  faction: number
  /** false = sole trader (the slot exists but is not charter-eligible). */
  founded: boolean
  charterEligible: boolean
  name: string
  /** 3-4 letters, unique against the faction tickers. */
  ticker: string
  livery: LiveryColors
  logo: LogoRecipe | null
  /** −1 = not founded. */
  foundedDay: number
  /** MILESTONE bits, and the faction day each was reached (−1 = not yet). */
  milestones: number
  milestoneDay: Int32Array
  /** Per system (claims the player holds): the manual tariff 0..0.1 (−1 = automatic) and DOCKING_RIGHTS. */
  tariff: Float32Array
  docking: Uint8Array
  /** Per faction (capacity): the rival threat feature (P12), 0..1. */
  threat: Float32Array
  /** Wallet credits owed to the Career (+) or taken from it (−) since the last advance (`walletDelta`). */
  pendingWallet: number
  /** Ships ever owned (the first-extra-ship milestone, the sole-trader cap). */
  shipsEver: number
}

/**
 * Player station charters and commissions (M6 spec §7.2), a slot pool of `caps.playerStations`. A commission is
 * an M4 construction site while it builds (`site`) and an M4 station once built (`station`).
 */
export interface CommissionStore {
  capacity: number
  count: number
  high: number
  free: Int32Array
  freeTop: number
  alive: Uint8Array
  /** Per system: unused station charters the player holds there. */
  charters: Uint8Array
  sys: Uint16Array
  /** STATION_TYPE. */
  type: Uint8Array
  /** The built level (0 while the first build runs) and the level being built (= level when idle). */
  level: Uint8Array
  targetLevel: Uint8Array
  /** The M4 site index while building (−1) and the station index once built (−1). */
  site: Int32Array
  station: Int32Array
  /** 1 = inputs are auto-bought from the company account (commission.json `autoBuy`). */
  autoBuy: Uint8Array
  /** PRICE_POLICY and TRADE_PERMISSION. */
  pricePolicy: Uint8Array
  permission: Uint8Array
  /** `slot × WARE_COUNT + ware`: manual minimum and maximum unit prices (0 = no limit). */
  priceMin: Float32Array
  priceMax: Float32Array
  /** Manager captain (−1 = none). */
  manager: Int16Array
  commissionedDay: Int32Array
  builtDay: Int32Array
  earnedToday: Float64Array
  earnedTotal: Float64Array
  /** Commission id = `pst-<seq>`. */
  seq: Uint32Array
  nextSeq: number
}

/** A dead captain on the memorial (M6 spec §5). */
export interface MemorialEntry {
  captainId: string
  name: string
  portrait: number
  shipName: string
  shipClass: ShipClassName
  systemId: string
  day: number
  /** The killer faction, or null (unknown). */
  killer: CorpFactionId | null
  runs: number
  kills: number
  daysServed: number
}

/** The memorial: the newest `capacity` entries (captains.json `memorial`), oldest dropped first. */
export interface MemorialRing {
  capacity: number
  entries: MemorialEntry[]
  /** Deaths ever recorded. */
  total: number
}

/** A fleet alert (M6 spec §8): the UI offers *jump to* and *send wing* for `assetAttacked`. */
export type FleetAlertKind = 'assetAttacked' | 'shipLost' | 'captainDied' | 'captainDeserted' | 'strike' | 'wagesLate' | 'stationBuilt'
export const FLEET_ALERT_KINDS = [
  'assetAttacked',
  'shipLost',
  'captainDied',
  'captainDeserted',
  'strike',
  'wagesLate',
  'stationBuilt',
] as const satisfies readonly FleetAlertKind[]

export interface FleetAlert {
  kind: FleetAlertKind
  t: number
  systemId: string
  /** The ship (`ps-<seq>`), station (`pst-<seq>`) and captain concerned, null = none. */
  shipId: string | null
  stationId: string | null
  captainId: string | null
  /** The attacker, when known. */
  attacker: CorpFactionId | null
  text: string
}

/** A captain log line (M6 spec §5, §8; templates in wire-m6.json `logs`). */
export interface FleetLogEntry {
  id: number
  t: number
  day: number
  /** The M6 EV type it came from. */
  type: number
  shipId: string | null
  captainId: string | null
  systemId: string | null
  text: string
}

/** The fleet feed (plan P13): the newest `capacity` log lines (wire-m6.json `feedKeep`), oldest dropped first. */
export interface FleetFeed {
  capacity: number
  entries: FleetLogEntry[]
  nextId: number
}

// ---------------------------------------------------------------------------------------------
// M6 views (M6 spec §8; plan P14 builds them on request, never per tick)
// ---------------------------------------------------------------------------------------------

/** A standing order at the boundary (the Fleet screen, `setOrder`). Omitted fields take the order's defaults. */
export interface StandingOrder {
  order: OrderName
  roe: RoeName
  stops: StopName[]
  /** Numeric parameters by ORDER_PARAM name (range-checked against orders.json `limits`). */
  params: Partial<Record<OrderParamName, number>>
  /** Allowed and blocked wares (trade wares, mined ores); empty `wares` = any. */
  wares: WareId[]
  blockWares: WareId[]
  /** Explicit area systems (AREA_MODE.systems); empty = N jumps from home. */
  systems: string[]
  sellPolicy?: SellPolicyName
  /** Station ids: the `myStation` sink, the supply target or ferry destination. */
  sellStation?: string
  target?: string
  priority?: WareId[]
  patrolRule?: PatrolRuleName
  laneRelay?: string
  /** A ship id (`ps-<seq>`) or 'player'. */
  escort?: string
  stopDay?: number
}

export interface CaptainSummary {
  id: string
  name: string
  portrait: number
  state: CaptainStateName
  skills: Record<SkillName, number>
  traits: CaptainTraitName[]
  wage: number
  loyalty: number
  xp: Record<SkillName, number>
  /** The ship it commands, or the station it manages. */
  shipId: string | null
  stationId: string | null
  origin: FactionId
  recoverUntilDay: number | null
}

export interface FleetShipView {
  id: string
  name: string
  hull: string
  shipClass: ShipClassName
  captain: CaptainSummary | null
  order: StandingOrder
  preset: string | null
  status: ShipStatusName
  systemId: string | null
  stationId: string | null
  /** The station's display name (the core's own, so stations built in play have theirs too); null when not at one. */
  stationName: string | null
  cargo: WareLot | null
  hp: number
  insured: boolean
  /**
   * M9 (CH-12b): a claimed ship (`registerClaimedShip` made it; the `hulk:<key>` world flag). Absent = false (views
   * built before M9, test fixtures).
   */
  claimed?: boolean
  /** M9: false while a claimed ship is unregistered (worker `insured` 2, until it docks). Absent = true. */
  registered?: boolean
  /** M9: the hull's flaws bitmask (`FleetStore.flaws`; bits 12–15 the First Rush edge). Absent = 0. */
  flaws?: number
  /** M9 (CH-8): the hull's value factor (`FleetStore.valueMult`), 1 = pristine. Absent = 1. */
  valueMult?: number
  /**
   * M9 gap: the ship's agent flies in a wing group (an escort's group, as the escort or the ship followed;
   * `AgentStore.group` ≥ 0). The worker refuses scrapping and the transfer service `busy` then. Absent = false.
   */
  inWingGroup?: boolean
  /**
   * M10 (D-TBD, the pair load check): the slot is out of the pool with the cradle pair (`CARRIED_UNTIL`: the parked L
   * while its S flies, or the S in the flown L's cradle). Absent = false (views built before, test fixtures).
   */
  carried?: boolean
  /**
   * D-TBD (the pair load check): the worker's flown ship (`BOARDED_UNTIL`; `boardedSlot`). At most one ship has it; none
   * when the player flies the own (lease) ship. Absent = false.
   */
  flown?: boolean
  group: number
  /** Signed credits the ship earned today and on the last full day: the Corporation P&L ship line's basis. */
  earnedToday: number
  earnedYesterday: number
  earnedPerDay: number
  earnedTotal: number
}

export interface FleetView {
  day: number
  ships: FleetShipView[]
  /** strategic.json `caps.playerShips`, or the sole-trader cap while not founded. */
  cap: number
  soleTrader: boolean
  /**
   * M10 P5a3c: the default name the next ship allocated gets (`defaultShipName` at the next serial; a pure read, no
   * rng draw). The shop shows it in its name field. Absent from older hosts and fakes.
   */
  nextName?: string
}

export interface CaptainView {
  /** The roster at the station asked for (empty elsewhere). */
  stationId: string | null
  roster: CaptainSummary[]
  hired: CaptainSummary[]
  memorial: MemorialEntry[]
  hiredCap: number
}

/** One P&L line: a ship's or station's income, or a cost category (M6 spec §8). */
export interface PnlLine {
  key: string
  label: string
  /** Signed credits, the last full day and today. */
  yesterday: number
  today: number
}

export interface CorpView {
  founded: boolean
  name: string
  ticker: string
  livery: LiveryColors
  logo: LogoRecipe | null
  foundedDay: number | null
  treasury: number
  /** Income by ship and station; costs by category (wages, insurance, fees, construction, losses). */
  income: PnlLine[]
  costs: PnlLine[]
  net: { yesterday: number; today: number }
  claims: { systemId: string; tariff: number; docking: DockingRightsName; score: number }[]
  voteWeight: number
  milestones: { id: MilestoneName; day: number | null }[]
}

export interface PlayerStationView {
  id: string
  /** The display name (M6 P34): the M4 station's name once built, else the name it will be built with. */
  name: string
  /** The M4 station id once built, else null. */
  stationId: string | null
  systemId: string
  type: StationTypeName
  level: number
  targetLevel: number
  /** The M4 site while building, else null. */
  construction: SiteView | null
  station: StationView | null
  pricePolicy: PricePolicyName
  priceLimits: { ware: WareId; min: number | null; max: number | null }[]
  permission: TradePermissionName
  manager: CaptainSummary | null
  autoBuy: boolean
  earnedToday: number
  earnedTotal: number
  /** The next level's cost and days, null at the max level. */
  upgrade: { level: number; cost: Partial<Record<WareId, number>>; days: number } | null
}

/** One approved site of a system (M6 P34; `commission.ts approvedSites`). */
export interface CommissionSiteView {
  /** The `commissionStation` command's `site`. */
  index: number
  anchor: 'field' | 'body' | 'relay'
  /** Float64 system metres. */
  pos: [number, number, number]
  used: boolean
}

/** A system as the commissioning screen sees it (M6 P34). */
export interface CommissionSystemView {
  systemId: string
  /** The claim holder (`'player'` = the corporation), null when unclaimed. */
  holder: CorpFactionId | null
  /** Unused charters the corporation holds here. */
  charters: number
  /** Live commissions (building or built) here. */
  stations: number
  sites: CommissionSiteView[]
  /** The corporation's manual tariff while it holds the claim; null = automatic or not held. */
  manualTariff: number | null
}

/** One station type at one level (templates.json `buildCost`, commission.json `buildDays`). */
export interface CommissionLevelView {
  level: number
  cost: Partial<Record<WareId, number>>
  days: number
}

/**
 * The commissioning view (M6 P34, spec §7.2): what a charter costs, the caps, every system's holder, charters and
 * approved sites, and the station types' levels. Built on request.
 */
export interface CommissionView {
  founded: boolean
  treasury: number
  charterPrice: number
  minStanding: number
  maxPerSystem: number
  /** Live commissions plus unused charters (the `caps.playerStations` pool). */
  used: number
  capacity: number
  systems: CommissionSystemView[]
  types: { type: StationTypeName; levels: CommissionLevelView[] }[]
}

/** An away-report fleet line (M6 spec §8; plan P13). */
export interface AwayFleetLine {
  kind: 'earning' | 'loss' | 'captain' | 'claim' | 'station' | 'milestone'
  day: number
  text: string
  shipId?: string
  captainId?: string
  systemId?: string
  amount?: number
}

export interface AwayFleetSummary {
  earnings: number
  wages: number
  costs: number
  net: number
  shipsLost: number
  lossValue: number
  captainsLost: number
  claimsGained: number
  claimsLost: number
  /**
   * D-459 bug 3: each claim gained or lost, by system (index order: gains, then losses), so the report can name it.
   * Absent in reports built before it.
   */
  claimChanges?: AwayClaimChange[]
}

/** A claim the corporation gained or lost during an absence: the system and, for a loss, who holds it now. */
export interface AwayClaimChange {
  systemId: string
  /** The system's display name. */
  system: string
  change: 'gained' | 'lost'
  /** The new holder's public name for a loss ('' = lapsed, nobody holds it). */
  holder: string
}

// ---------------------------------------------------------------------------------------------
// M6 commands (M6 spec §4-§7; plan P15 joins `M6Command` into `StrategicCommand` and routes it)
// ---------------------------------------------------------------------------------------------

export type M6DevCommand =
  | { type: 'dev'; op: 'grantShip'; hull: string; stationId: string }
  | { type: 'dev'; op: 'grantCredits'; amount: number }
  | { type: 'dev'; op: 'forceFounding' }
  | { type: 'dev'; op: 'forceShipLoss'; shipId: string }
  | { type: 'dev'; op: 'forceDesertion'; captainId: string }
  | { type: 'dev'; op: 'forceCaptainDeath'; captainId: string }
  | { type: 'dev'; op: 'finishCommission'; commissionId: string }
  /** M9 CH-12d: a disabled (claim 1) off-screen wreck of a claimable hull in `systemId` (default hull: the first eligible). */
  | { type: 'dev'; op: 'forceDisabledLoss'; systemId: string; hull?: string }

/**
 * The M6 commands. `wallet` is the Career's personal wallet at send time (the Career is its source of truth);
 * money the command takes from or gives to it comes back as `CommandResult.walletDelta`.
 */
export type M6Command =
  /** M10 P5a3b: `name` (optional) is the player's name for the new ship; absent = the seeded default (today's command). */
  | { type: 'buyShip'; yardId: string; hull: string; standing: number; payFrom: 'company' | 'wallet'; wallet: number; name?: string }
  /** M10 P5a3b: renames owned fleet ship `shipId` (player only; 1 to 24 characters, trimmed, unique in the fleet). */
  | { type: 'renameShip'; shipId: string; name: string }
  | { type: 'setOrder'; shipId: string; order: StandingOrder }
  | { type: 'applyPreset'; shipIds: string[]; preset: string }
  | { type: 'setInsurance'; shipId: string; insured: boolean; leaseOwned: boolean }
  | { type: 'ferryShip'; shipId: string; stationId: string }
  | { type: 'transferService'; shipId: string; stationId: string; wallet: number }
  | { type: 'boardShip'; shipId: string }
  | { type: 'leaveShip'; stationId: string }
  /** M6 P24b: the boarded (flown) fleet ship died uninsured: its slot is freed as a loss. */
  | { type: 'loseBoardedShip'; shipId: string }
  | { type: 'hireCaptain'; stationId: string; candidate: number; wallet: number }
  | { type: 'fireCaptain'; captainId: string }
  | { type: 'assignCaptain'; captainId: string; shipId: string | null; stationId: string | null }
  | { type: 'payWages'; captainId: string | null; wallet: number }
  | { type: 'foundCorp'; name: string; ticker: string; livery: LiveryColors; logo: LogoRecipe; leaseOwned: boolean; wallet: number }
  | { type: 'setLivery'; livery: LiveryColors }
  | { type: 'companyTransfer'; amount: number; direction: 'toCompany' | 'toWallet'; wallet: number }
  | { type: 'buyCharter'; systemId: string; standing: number; wallet: number }
  | { type: 'commissionStation'; systemId: string; site: number; stationType: StationTypeName; level: number; autoBuy: boolean }
  | { type: 'upgradeStation'; commissionId: string }
  | { type: 'setPricePolicy'; commissionId: string; policy: PricePolicyName; limits: { ware: WareId; min: number | null; max: number | null }[] }
  | { type: 'setPermissions'; commissionId: string; permission: TradePermissionName }
  | { type: 'setManager'; commissionId: string; captainId: string | null }
  | { type: 'setClaimTariff'; systemId: string; tariff: number | null }
  | { type: 'setDockingRights'; systemId: string; rights: DockingRightsName }
  | M6DevCommand
export type M6CommandType = M6Command['type']

export type M6CommandError =
  | 'unknownShip'
  | 'unknownCaptain'
  | 'unknownCommission'
  | 'unknownHull'
  | 'noFunds'
  | 'capReached'
  | 'standingTooLow'
  | 'notFounded'
  | 'alreadyFounded'
  | 'noOwnership'
  | 'tickerTaken'
  | 'notOwned'
  | 'notDocked'
  | 'notAllowed'
  | 'noCharter'
  | 'claimed'
  | 'maxLevel'
  | 'busy'
  /** M10 P4a3b: the shipyard's owners won't build for the player (war, or an L/XL hull at skirmish). */
  | 'embargo'
  /** M10 P5a3b: a ship name that is empty, too long or has outer whitespace. */
  | 'badName'
  /** M10 P5a3b: another ship in the fleet already has that name. */
  | 'nameTaken'

// ---------------------------------------------------------------------------------------------
// M6 save v3 (plan P1 schema and stub, P16 the real v3; typed-array stores are base64 columns)
// ---------------------------------------------------------------------------------------------

/** Everything M6 adds to the save. */
export interface FleetSave {
  /** FleetStore columns trimmed to `high`, the names and the serial. */
  fleet: { high: number; nextSeq: number; names: string[]; cols: Record<string, string> }
  /** CaptainStore hired-slot columns (rosters re-roll from the seed and the day) and the serial. */
  captains: { hiredCount: number; nextSeq: number; cols: Record<string, string> }
  corp: {
    faction: number
    founded: boolean
    charterEligible: boolean
    name: string
    ticker: string
    livery: LiveryColors
    logo: LogoRecipe | null
    foundedDay: number
    milestones: number
    milestoneDay: number[]
    pendingWallet: number
    shipsEver: number
    /** Base64 columns: tariff, docking, threat. */
    cols: Record<string, string>
  }
  commissions: { high: number; nextSeq: number; cols: Record<string, string> }
  memorial: { entries: MemorialEntry[]; total: number }
  feed: { entries: FleetLogEntry[]; nextId: number }
}

export interface StrategicSaveV3 extends Omit<StrategicSaveV2, 'version'> {
  version: 3
  fleet: FleetSave
}

// =============================================================================================
// M7: The First Rush, the worker side (M7 plan §2 "worker additions", S16). Append-only. The main thread mirrors
// the commands in `src/story/types.ts` `StoryCommand` (S0) and reads `StoryView` as `StoryWorldView`; the core may
// not import `src/story/**` (spec §3.3), so the name lists live here too and `m7.test.ts` keeps the two in step.
// S16 allocates the stores, routes the commands and calls the day stubs; S17–S24 fill `src/strategic/story/*`.
// =============================================================================================

/*
 * M7 event fields (unused fields as for M4/M5):
 * - `storyTierDrop`: sys = SYS_GLOBAL, cause = the claimed tier, value = the stability after, qty = the drop.
 * - `grievanceSet`: faction = a, other = b (FACTION_PLAYER = the player), value = the floor, aux = the end online day.
 * - `floorExtended`: faction = a (FACTION.guardian for the aggression floor), other = b, aux = the new end online day.
 * - `repealProposed`: faction = the sponsor, cause = MEASURE, aux = the vote seq.
 * - `stationRestored` / `stationDark` / `stationReverted`: sys, station (−1 once reverted), faction = the owner.
 * - `registryChallenge`: sys, station, faction = the challenger (tethys), other = the owner, aux = the end online day.
 * - `registrationRefused` (S24 reconcile): a registration the petty registry held was refused on its due day; sys,
 *   faction = the owner (the corporation's slot, or FACTION_NONE), value = the fee not charged, aux = the POI's index
 *   in the system's POI list. The main thread sets its Career record back to `lit`.
 */

/**
 * M8 event fields (plan 2026-10-04 SNK-6a, D-TBD T30; unused fields as for M4/M5):
 * - `worksOpened`: a faction opened a surplus works site (`factions/actions.ts` `ACTION_WORKS`); sys, station = the
 *   station a level-up works re-levels (−1 for a new build), faction = the owner, cause = the works kind (its index in
 *   `sinks/schema.ts` `WORKS_KINDS`), value = the candidate's cost, aux = the site seq. Never `factionAction` with
 *   cause 8: that event's cause is an `ACTION` code (`claims/holder.ts` and `metrics.ts` read it).
 */

/**
 * RD-B2 event fields (liveliness plan 2026-10-13; unused fields as for M4/M5):
 * - `clanReformed`: a disbanded clan re-formed (`outlaws/clans.ts` `reformClans`, key `clans.reform`); sys = its new
 *   system, station = its fence (moved to the new node), faction = FACTION.raider, value = the node's unguarded-traffic
 *   score, aux = the clan index.
 */

/** Wire outlets (M7 spec §12.3), each with slanted headlines (S17). */
export const STORY_WIRE_OUTLETS = ['authorityGazette', 'mutualBulletin', 'tethysComms', 'candleRelay'] as const
export type StoryWireOutlet = (typeof STORY_WIRE_OUTLETS)[number]

/** Retaliation rows (M7 spec §9); the worker keeps one bit per row per faction (`setRetaliation`). */
export const STORY_RETALIATION_ROWS = [
  'principalDue',
  'assetRecoveryHunt',
  'insuranceCancelled',
  'dockingRefused',
  'refinerySalesRefused',
  'boardsClosed',
  'interdiction',
  'componentsRefused',
  'yardsRefused',
  'claimPressure',
  'sabotage',
  'fleetNotesCalled',
] as const
export type StoryRetaliationRow = (typeof STORY_RETALIATION_ROWS)[number]

/** The player corporation's ballot stance per measure (M7 spec §8.2). Code 0 = none set (the default). */
export const STORY_BALLOT_STANCES = ['for', 'against', 'abstain'] as const
export type StoryBallotStance = (typeof STORY_BALLOT_STANCES)[number]
export const BALLOT_STANCE = { none: 0, for: 1, against: 2, abstain: 3 } as const

/** Contestable claim states (M7 spec §12.2, §16.6). `none` clears. */
export const STORY_CLAIM_STATES = ['none', 'underReview', 'harbouring'] as const
export type StoryClaimState = (typeof STORY_CLAIM_STATES)[number]
export const CLAIM_STATE = { none: 0, underReview: 1, harbouring: 2 } as const

/** Mission-owned traffic (`spawnStoryShip`; never ambient spawns). */
export const STORY_SHIP_ROLES = ['assetRecovery', 'lesseeBarge', 'prisonerTransport', 'vossYacht', 'meridianCourier', 'missionRig', 'varroContractors'] as const
export type StoryShipRole = (typeof STORY_SHIP_ROLES)[number]

/** Who registers a restarted derelict (M7 spec §16.5). */
export const STORY_REGISTRANTS = ['title', 'corporation', 'handover'] as const
export type StoryRegistrant = (typeof STORY_REGISTRANTS)[number]

export const STORY_MISSION_IDS = ['m1', 'm2', 'm3', 'm4'] as const
export type StoryMissionId = (typeof STORY_MISSION_IDS)[number]

/** How a player station came to be (M7 spec §16.5): commissioned through M6, or a restarted First Rush derelict. */
export const COMMISSION_KIND = { commissioned: 0, reactivated: 1 } as const

/** A faction or the player (pressure, grievances and transfers involve the player's side too). */
export type StoryParty = FactionId | 'player'

/** The dev story commands (`state.dev` only). */
export type M7DevCommand = { type: 'storyDev'; op: 'setOnlineDay'; day: number } | { type: 'storyDev'; op: 'clearStory' }

/**
 * The M7 worker commands (plan §2; the exact shapes of `src/story/types.ts` `StoryCommand`). The four the story
 * reuses from M5 (`setStoryTier`, `setWorldFlag`, `postWire`, `proposeCharterVote`) are `CharterCommand`s; the
 * story bridge (S26) translates its flat `postWire` to the M5 `item` shape. `storyDev` needs `state.dev`.
 */
export type M7Command =
  | { type: 'setStoryClock'; storyMult: number }
  | { type: 'storyTierDrop'; tier: number; drop: number; cause: string }
  | { type: 'seedPressure'; a: StoryParty; b: StoryParty; amount: number }
  | { type: 'setGrievance'; a: StoryParty; b: StoryParty; floor: number; untilOnlineDay: number }
  | { type: 'setAggressionFloor'; faction: FactionId; value: number; untilOnlineDay: number }
  | { type: 'setRetaliation'; faction: FactionId; rows: StoryRetaliationRow[] }
  | { type: 'setPlayerBallot'; measure: MeasureName; stance: StoryBallotStance }
  | { type: 'setClaimState'; scope: 'claim' | 'commission'; id: string; state: StoryClaimState; untilOnlineDay: number | null }
  | { type: 'spawnStoryShip'; key: string; role: StoryShipRole; systemId: string; missionId?: StoryMissionId }
  | { type: 'hiddenTransfer'; from: StoryParty; to: FactionId; amount: number }
  | { type: 'seedBounty'; faction: FactionId; amount: number; reason: string }
  | { type: 'wipeBounties'; faction: FactionId; olderThanDay: number; cap: number }
  | { type: 'restoreBounties'; faction: FactionId; amount: number }
  | { type: 'registerHoldfastClaims'; registered: boolean }
  | { type: 'setCharterWaiver'; charters: number; waiveMinStanding: boolean }
  | { type: 'restoreDerelict'; poiId: string; systemId: string; template: string; owner: StoryParty }
  | {
      type: 'registerReactivation'
      poiId: string
      registrant: StoryRegistrant
      recipient?: FactionId
      fee: number
      /** S24: the site carries a Holdfast generational claim marker (spec §16.6). */
      marker?: boolean
      /** S24: a corporation runs the marked site with its family (`holdfastShare` of its income to the Holdfast). */
      withFamily?: boolean
      /** S24: the petty registry's wait (spec §16.6): the registration takes effect this many online days later. */
      waitOnlineDays?: number
      /** S26: the story bridge re-applying a registration the Career already holds (a fresh Reach): no fee, no finder's fee, no standings, no restart influence, no event. S24's marker standings, family-share setup and registry challenge are not re-run either. */
      reapply?: boolean
    }
  /** S24: the player pays the legal fee that ends a Tethys registry challenge early (spec §16.6). */
  | { type: 'settleRegistryChallenge'; poiId: string; fee: number }
  | { type: 'supplyStation'; poiId: string; good: string; qty: number }
  /**
   * S39d: lowers the newest pending vote on `measure` by `drop` evidence weight (clamped at 0). The story link sends
   * it when the ladder moves Expose → Guardians (spec §8.3: the sponsor's pilot flies with the guardians).
   */
  | { type: 'lowerVoteWeight'; measure: MeasureName; drop: number }
  | M7DevCommand
export type M7CommandType = M7Command['type']

/** M7 refusals: a command whose worker half an S17–S24 task has not filled yet, or an unknown POI. */
export type M7CommandError =
  | 'notImplemented'
  | 'unknownPoi'
  /** S24: a Holdfast-marked site after Hale's deal registered the claims (self-registration refused). */
  | 'markedRegistered'
  /** S24: running a marked site with the family needs Holdfast standing ≥ `familyMinStanding` after Hale's deal. */
  | 'familyStanding'
  /** S24: no registry challenge stands against that restart. */
  | 'noChallenge'
  /** S39d: `lowerVoteWeight` found no pending vote on that measure (resolved already, or never proposed). */
  | 'noPendingVote'

/** Grievance terms (M7 spec §12.3): a capped SoA list of `{ a, b, floor, untilOnlineDay }`. */
export interface StoryGrievanceStore {
  capacity: number
  count: number
  /** Faction indices; FACTION_PLAYER = the player's side (resolved to the corporation slot when it exists). */
  a: Uint8Array
  b: Uint8Array
  floor: Float32Array
  /** The online day the term ends. */
  until: Int32Array
}

/** A restarted First Rush derelict the worker holds (M7 spec §16.4–§16.5; S20 fills it). */
export interface StoryRestoredPoi {
  poiId: string
  systemId: string
  template: string
  owner: StoryParty
  /** The M4 station index once built (−1 while the refit runs or after a revert). */
  station: number
  registrant: StoryRegistrant | null
  recipient?: FactionId
  /** The faction day it was restored. */
  day: number
  /** Online days without fuel cells (S23: dark after `darkAfterDays`, reverted after `revertDays`). */
  darkDays: number
  dark: boolean
  /** S24: a Holdfast-marked site (spec §16.6). */
  marker?: boolean
  /** S24: run with the family; `familyPaid` is the commission's `earnedTotal` already shared with the Holdfast. */
  withFamily?: boolean
  familyPaid?: number
  /** S24: a registration the petty registry holds until its due online day (spec §16.6). */
  pending?: StoryPendingRegistration
}

/** A registration waiting out the petty registry (S24; `reactivation.ts` applies it on its due online day). */
export interface StoryPendingRegistration {
  registrant: StoryRegistrant
  recipient?: FactionId
  fee: number
  marker?: boolean
  withFamily?: boolean
  dueOnlineDay: number
}

/** A pilot's restart title (M7 spec §16.5; S23 keeps it alive): lapses at `lapseOnlineDay` without a delivery. */
export interface StoryTitle {
  poiId: string
  lapseOnlineDay: number
}

/** A Tethys registry challenge (M7 spec §16.6; S24): the station is under review until `untilOnlineDay`. */
export interface StoryChallenge {
  poiId: string
  untilOnlineDay: number
}

/** A mission-owned ship (`spawnStoryShip`; S19): `agentSeq` −1 until spawned. Keyed so a re-apply never doubles it. */
export interface StoryShipRecord {
  key: string
  role: StoryShipRole
  systemId: string
  missionId?: StoryMissionId
  agentSeq: number
}

/** The M7 worker store (`state.story`; plan §2, S16 allocates, S17–S24 write). Day work only, never per tick. */
export interface StoryStore {
  /** Online faction days counted (a catch-up day is not online; plan §2). */
  onlineDay: number
  /** Runtime only: true while the catch-up runs (its days are not online). Never saved. */
  offline: boolean
  /** Runtime only: online days ended since the host last read them (`AdvanceResult.onlineDays`). Never saved. */
  pendingOnlineDays: number
  grievances: StoryGrievanceStore
  /** The guardian aggression floor (`setAggressionFloor`): 0 = none; ends at `untilOnlineDay`. */
  guardianFloor: { value: number; untilOnlineDay: number }
  /** Per MEASURE: BALLOT_STANCE. */
  ballot: Uint8Array
  /** The Tethys repeal push (S18): proposed on `dueOnlineDay` (−1 = none) after the ban passed on `banDay` (−1). */
  repeal: { dueOnlineDay: number; banDay: number }
  /** Per faction (capacity): one bit per STORY_RETALIATION_ROWS row. */
  retaliation: Uint16Array
  /** Per system: CLAIM_STATE of the holder's claim, and the online day it ends (−1 = until cleared). */
  claimState: Uint8Array
  claimStateUntil: Int32Array
  /** Per commission slot (the commission store's capacity): COMMISSION_KIND, CLAIM_STATE and `reviewUntil` (−1). */
  commissionKind: Uint8Array
  commissionState: Uint8Array
  reviewUntil: Int32Array
  restored: StoryRestoredPoi[]
  titles: StoryTitle[]
  challenges: StoryChallenge[]
  ships: StoryShipRecord[]
  /** Story rigs shown for the ramp (S19; a view count, never extraction). */
  rigs: number
  /** Tethys's AI-only tetherstone income per faction day, a ring of strategic.json `story.incomeDays` (S19 writes it). */
  tethysIncome: Float64Array
  tethysIncomeHead: number
  /** Hale's deal (M7 spec §7): charters left with the standing gate waived. */
  charterWaiver: { charters: number; waiveMinStanding: boolean }
  /** Hale's deal (c): the Holdfast generational claims are registered (S24 refuses marked self-registration). */
  holdfastClaimsRegistered: boolean
  /**
   * Stations of reverted restarts (undockable unpowered hulks) that keep the fuel-cell input upkeep gave them
   * (`story/upkeep.ts ensureFuelInput`). The save re-applies it to them and the built restarts before it reads the
   * station-ware columns, whose mask follows the trade bits (D-TBD).
   */
  hulks: number[]
  /** M7 S43, runtime only: reactivation notes for `AdvanceResult.storyReactivations` (drained every advance). Never saved. */
  pendingReactivationNotes: StoryReactivationNote[]
}

/**
 * M7 S43: a reactivation change the main thread mirrors on its Career record (`story-link/reactivation/notes.ts`):
 * - `converted`: a title became a corporation station at founding (record → `station`).
 * - `released`: a title over `title.max` (or with no M6 station) at founding, unregistered and awaiting a hand-over.
 * - `reverted`: the restart reverted to a derelict (`stationReverted`).
 * - `refused`: the petty registry refused a held registration on its due day (`registrationRefused`).
 */
export const STORY_REACTIVATION_NOTES = ['converted', 'released', 'reverted', 'refused'] as const
export type StoryReactivationNoteKind = (typeof STORY_REACTIVATION_NOTES)[number]
export interface StoryReactivationNote {
  kind: StoryReactivationNoteKind
  poiId: string
}

/** The small worker view the main-thread story reads (`src/story/types.ts` `StoryWorldView`). */
export interface StoryView {
  /** The worker's faction day. */
  day: number
  onlineDay: number
  stability: number
  stage: GateStageName
  /** The player corporation's faction id once founded (`'player'`, the M6 slot), else null. */
  corpFaction: CorpFactionId | null
  /** Tethys's AI-only tetherstone income over the last 10 faction days (the stipend's base, T17). */
  tethysAiTetherIncome10d: number
  storyRigs: number
  /** POI ids of reactivated stations the worker holds. */
  reactivatedStations: string[]
  /**
   * M7 S37 (additive): the mission-owned ships spawned so far (`state.story.ships` with `agentSeq` ≥ 0), so the main
   * thread can name a killed agent (`ag-<seq>`) as its story ship key (`shipKilled.storyShip`).
   */
  storyShips?: { key: string; role: StoryShipRole; agentSeq: number }[]
  /**
   * M7 S39d (additive): the newest tetherstone ban vote's ballots once it is resolved (its yes and no voting weights,
   * `votes.ts resolveVote`), for the finale epilogue's `{for}` and `{against}`. Absent while the newest ban vote is
   * pending, or when none was ever proposed.
   */
  banBallots?: { for: number; against: number }
  /** M7 S43 (additive): the world flag `candle_dark` is set (the Candle went dark, Blackmail); absent otherwise. */
  candleDark?: boolean
}

// ---------------------------------------------------------------------------------------------
// M7 save v4 (S16): the M7 block. Small records as JSON; per-system and per-commission columns as base64.
// ---------------------------------------------------------------------------------------------

export interface StorySave {
  onlineDay: number
  grievances: { a: StoryParty; b: StoryParty; floor: number; untilOnlineDay: number }[]
  guardianFloor: { value: number; untilOnlineDay: number }
  ballot: Partial<Record<MeasureName, StoryBallotStance>>
  repeal: { dueOnlineDay: number; banDay: number }
  retaliation: Partial<Record<FactionId, StoryRetaliationRow[]>>
  restored: StoryRestoredPoi[]
  titles: StoryTitle[]
  challenges: StoryChallenge[]
  ships: StoryShipRecord[]
  rigs: number
  tethysIncomeHead: number
  charterWaiver: { charters: number; waiveMinStanding: boolean }
  holdfastClaimsRegistered: boolean
  /** `StoryState.hulks`; omitted when empty (D-TBD). */
  hulks?: number[]
  /**
   * Base64 columns: `claimState`, `claimStateUntil` (per system); `commissionKind`, `commissionState`, `reviewUntil`
   * (per commission slot below the store's `high`); `tethysIncome` (the ring).
   */
  cols: Record<string, string>
}

export interface StrategicSaveV4 extends Omit<StrategicSaveV3, 'version'> {
  version: 4
  story: StorySave
}

/**
 * The M8 record (plan §0 item 5; written by SNK-4a, extended by the later v5 cards before gate A): v4 plus the agent
 * column `agents.cols.mag` (the munitions magazine) and each wreck's `owner` (MC-f5c). `migrateV4toV5` writes every
 * live magazine full and every wreck owner −1.
 */
export interface StrategicSaveV5 extends Omit<StrategicSaveV4, 'version' | 'wrecks'> {
  version: 5
  wrecks: SavedOffscreenWreck[]
  /** The world's field-mix version (MC-a4): 0 = `oreWeights`, 1 = `mineralWeights` × grade. `migrateV4toV5` writes 0. */
  mineralsVersion: MineralsVersion
}

/**
 * The M9 record (plan 2026-10-05 §5, CH-1a; CH-8 extends the same version): v5 plus each wreck record's optional
 * `claim` (`OffscreenWreck.claim`, absent = 0) and the fleet columns `flaws` and `valueMult` (`fleet.fleet.cols`,
 * base64, so the type does not name them). `migrateV5toV6` writes `flaws` 0 and `valueMult` 1 for every fleet slot.
 */
export interface StrategicSaveV6 extends Omit<StrategicSaveV5, 'version'> {
  version: 6
}

/**
 * The 5(e) record (T32, T35; v7 migration calls §1.2, §5.3): v6's shape on the 19-ware layout (every per-ware column
 * `WARE_COUNT` wide, every saved ware index and mask in the live catalogue) plus two optional keys.
 * - `layoutFrom`: input only. `migrateV6toV7` writes 6: the masked station × ware and rollup columns, the line columns
 *   and the field reserves are still on the v6 layout, and the load re-strides them against a rebuilt v6 layout.
 *   `serializeState` never writes it, so the first native save is plain v7.
 * - `importFloor`: MC-e4's deep-median reference (`ImportFloorState`). Absent (a migrated save) = the floor ware's
 *   base price until the next faction day.
 */
export interface StrategicSaveV7 extends Omit<StrategicSaveV6, 'version'> {
  version: 7
  layoutFrom?: 6
  importFloor?: ImportFloorState
  /**
   * SUP-2b1 (D-751): the world's seeding revision (`world.seedRevision`). Written only when not 0; absent = 0 (every
   * save before SUP-2), so no version bump. M10 P3a's v8 migration must carry it.
   */
  seedRevision?: number
}

/**
 * The M10 P3a1 record (hull tiers spec §6.7): v7 plus `hullTable`, the hull id of each saved hull index (the agents'
 * and the fleet's `hull` columns, the notable ring's `orderPlaced`/`shipBuilt` causes). The load maps each saved index
 * to today's row by id; `migrateV7toV8` writes the frozen v1–v7 table (`LEGACY_HULL_IDS`). The day ledgers are
 * `LEDGER_COUNT_M10` (22) wide: the zero `royalty` column (D-771).
 */
export interface StrategicSaveV8 extends Omit<StrategicSaveV7, 'version'> {
  version: 8
  hullTable: string[]
}
